import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { SiteShell } from "@/components/site/SiteShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { claimFirstAdmin, adminDeliverOrder, adminUpdateOrder, signInAdminByPassword, verifyCurrentAdmin, adminGetOrderNotifySettings, adminSaveOrderNotifySettings, adminSetB2BTier, adminGetPushSmsSettings, adminSavePushSmsSettings, adminArchiveOrder, adminHardDeleteOrder, adminUploadInvoice } from "@/lib/admin.functions";
import { downloadOrderAssets } from "@/lib/order-bulk-download";
import { Archive, PackageOpen, Trash, Upload as UploadFileIcon, Wallet } from "lucide-react";
import { getMyAdminRole } from "@/lib/admin-active.functions";
import { useAdminPresence } from "@/hooks/use-admin-presence";
import { ModeratorNameGate, useModeratorIdentity } from "@/components/admin/ModeratorNameGate";

import { AdminActive } from "@/components/admin/sections/AdminActive";
import { WebMarketingPanel } from "@/components/admin/sections/WebMarketing";
import { WatermarkIconsPanel } from "@/components/admin/sections/WatermarkIcons";
import { B2BSubmissions } from "@/components/admin/sections/B2BSubmissions";
import { CommissionRequestsPanel } from "@/components/admin/sections/CommissionRequestsPanel";
import { SiteKeywordsPanel } from "@/components/admin/sections/SiteKeywordsPanel";
import { adminSetUserBlock } from "@/lib/user-block.functions";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, Users, ShoppingCart, FileText, CreditCard, MessageSquareQuote,
  HelpCircle, Image as ImageIcon, Phone, Settings, Search, Plus, Trash2, Pencil,
  Loader2, ExternalLink, BookOpen, BarChart3, Megaphone, MessageCircle, Truck, Database, Download,
  Eye, EyeOff, Plane, Receipt, AlertCircle, Ban, Sparkles, KeyRound, Copy, Briefcase,
} from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { InvoicePanel } from "@/components/admin/InvoicePanel";
import { OrderInvoiceEditor } from "@/components/admin/OrderInvoiceEditor";
import { useDownloadInvoice } from "@/hooks/use-download-invoice";
import { InvoiceDownloadControl } from "@/components/invoice/InvoiceDownloadControl";
import { CompactReceiptDialog } from "@/components/invoice/CompactReceiptDialog";
import { AdminTopNav } from "@/components/admin/AdminTopNav";
import { AdminOverview } from "@/components/admin/AdminOverview";
import { ReferralManagement } from "@/components/admin/sections/ReferralManagement";
import { LiveRankingDashboard } from "@/components/admin/LiveRankingDashboard";
import { SeoHealthPanel } from "@/components/admin/SeoHealthPanel";
import { SeoChecklistPanel } from "@/components/admin/SeoChecklistPanel";
import { DirectorySubmitPanel } from "@/components/admin/DirectorySubmitPanel";
import { NewOrderNotifier } from "@/components/admin/NewOrderNotifier";


export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin Dashboard — Online Tax VAT Bangladesh" },
      { name: "description", content: "Internal admin console for managing orders, packages, blog posts and customer operations at Online Tax VAT Bangladesh." },
      { name: "robots", content: "noindex, nofollow" },
      { name: "application-name", content: "Online Tax VAT Admin" },
      { name: "apple-mobile-web-app-title", content: "TaxVAT Admin" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "theme-color", content: "#2196F3" },
    ],
    links: [
      // Admin-scoped manifest overrides the site-wide one on /admin so the
      // installed app launches straight into the admin dashboard.
      { rel: "manifest", href: "/admin-manifest.webmanifest" },
      { rel: "apple-touch-icon", sizes: "192x192", href: "/pwa/icon-192x192.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/pwa/icon-192x192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/pwa/icon-512x512.png" },
      { rel: "canonical", href: "https://onlinetaxvat.com/admin" },
    ],
  }),
  component: AdminPage,
});

// -------- Generic CRUD configuration --------
type FieldType = "text" | "textarea" | "number" | "boolean" | "json" | "image" | "crop_image" | "select" | "tags" | "seo_multi" | "switch" | "datetime" | "percent";
type FieldDef = { key: string; label: string; type: FieldType; required?: boolean; options?: { value: string; label: string }[] };
type CrudTable = {
  table: string;
  label: string;
  icon: any;
  singleton?: boolean;
  listCols: string[];
  fields: FieldDef[];
  defaults?: Record<string, any>;
  orderBy?: { col: string; ascending: boolean };
};

const TABLES: CrudTable[] = [
  {
    table: "hero_section", label: "Hero", icon: ImageIcon, singleton: true,
    listCols: ["title", "badge_text", "active"],
    fields: [
      { key: "badge_text", label: "Badge", type: "text" },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
      { key: "cta_primary_text", label: "Primary CTA text", type: "text" },
      { key: "cta_primary_link", label: "Primary CTA link", type: "text" },
      { key: "cta_secondary_text", label: "Secondary CTA text", type: "text" },
      { key: "cta_secondary_link", label: "Secondary CTA link", type: "text" },
      { key: "image_url", label: "Image URL", type: "text" },
      { key: "active", label: "Active", type: "boolean" },
    ],
    defaults: { active: true },
  },
  {
    table: "campaign_settings", label: "Campaign Settings", icon: Megaphone, singleton: true,
    listCols: ["title", "enabled", "deadline"],
    fields: [
      { key: "enabled", label: "Campaign ON / OFF — instantly shows or hides the homepage campaign + corner badges sitewide", type: "switch" },
      { key: "deadline", label: "🕒 Target Deadline (drives the live countdown clock on the homepage)", type: "datetime", required: true },
      { key: "discount_percent", label: "💰 Global Discount % — single number that controls homepage badge AND locked checkout pricing (e.g. 10)", type: "percent" },
      { key: "badge_text", label: "Top-right badge (e.g. Get 10% Off 🎉)", type: "text" },
      { key: "status_label", label: "Status label (e.g. Coming Soon)", type: "text" },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "subtitle", label: "Subtitle", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "countdown_title", label: "Countdown title (e.g. Offer Ends In)", type: "text" },
      { key: "contact_button_label", label: "Contact button label", type: "text" },
      { key: "contact_url", label: "Contact URL (WhatsApp link)", type: "text" },
      { key: "register_button_label", label: "Register button label", type: "text" },
      { key: "register_url", label: "Register URL (e.g. /signup)", type: "text" },
    ],
    defaults: { enabled: true },
  },
  {
    table: "services", label: "Services", icon: FileText,
    listCols: ["title", "slug", "service_code", "display_order", "active"],
    fields: [
      { key: "service_code", label: "Service Code (e.g. Service001)", type: "text" },
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "title", label: "Title (English)", type: "text", required: true },
      { key: "title_bn", label: "Title (Bengali / বাংলা)", type: "text" },
      { key: "description", label: "Description (English)", type: "textarea" },
      { key: "description_bn", label: "Description (Bengali / বাংলা)", type: "textarea" },
      { key: "icon", label: "Icon name (lucide)", type: "text" },
      { key: "features", label: "Features — English (JSON array)", type: "json" },
      { key: "features_bn", label: "Features — Bengali / বাংলা (JSON array)", type: "json" },
      { key: "document_checklist", label: "Document Checklist for Invoice — English (JSON array of strings)", type: "json" },
      { key: "document_checklist_bn", label: "Document Checklist for Invoice — Bengali / বাংলা (JSON array of strings)", type: "json" },
      { key: "popup_title", label: "Popup title — English (optional)", type: "text" },
      { key: "popup_title_bn", label: "Popup title — Bengali / বাংলা (optional)", type: "text" },
      { key: "popup_message", label: "Popup message — English (text only, shown on Services page)", type: "textarea" },
      { key: "popup_message_bn", label: "Popup message — Bengali / বাংলা", type: "textarea" },
      { key: "popup_duration", label: "Popup duration in seconds (5–10 recommended)", type: "number" },
      { key: "popup_active", label: "Show popup message", type: "boolean" },
      { key: "display_order", label: "Order", type: "number" },
      { key: "active", label: "Active", type: "boolean" },
    ],
    defaults: { features: [], features_bn: [], document_checklist: [], document_checklist_bn: [], display_order: 0, active: true, popup_duration: 7, popup_active: false },
    orderBy: { col: "display_order", ascending: true },
  },
  {
    table: "packages", label: "Packages", icon: CreditCard,
    listCols: ["name", "service_code", "price", "popular", "active"],
    fields: [
      { key: "service_code", label: "Service Code (match a service, e.g. Service001)", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "name", label: "Name (English)", type: "text", required: true },
      { key: "name_bn", label: "Name (Bengali / বাংলা)", type: "text" },
      { key: "price", label: "Price (number or text e.g. 'Custom Pricing', 'Depends on File Type')", type: "text", required: true },
      { key: "currency", label: "Currency", type: "text" },
      { key: "description", label: "Description (English)", type: "textarea" },
      { key: "description_bn", label: "Description (Bengali / বাংলা)", type: "textarea" },
      { key: "delivery_days", label: "Delivery time — English (e.g. '5-6 business days')", type: "text" },
      { key: "delivery_days_bn", label: "Delivery time — Bengali / বাংলা", type: "text" },
      { key: "features", label: "Features — English (JSON array)", type: "json" },
      { key: "features_bn", label: "Features — Bengali / বাংলা (JSON array)", type: "json" },
      { key: "document_checklist", label: "Document Checklist — English (JSON array — required docs for invoice)", type: "json" },
      { key: "document_checklist_bn", label: "Document Checklist — Bengali / বাংলা (JSON array)", type: "json" },
      { key: "popup_title", label: "Popup title — English (optional)", type: "text" },
      { key: "popup_title_bn", label: "Popup title — Bengali / বাংলা (optional)", type: "text" },
      { key: "popup_message", label: "Popup message — English (text only, shown on Packages page)", type: "textarea" },
      { key: "popup_message_bn", label: "Popup message — Bengali / বাংলা", type: "textarea" },
      { key: "popup_duration", label: "Popup duration in seconds (5–10 recommended)", type: "number" },
      { key: "popup_active", label: "Show popup message", type: "boolean" },
      { key: "popular", label: "Popular", type: "boolean" },
      { key: "display_order", label: "Order", type: "number" },
      { key: "active", label: "Active", type: "boolean" },
    ],
    defaults: { currency: "BDT", features: [], features_bn: [], document_checklist: [], document_checklist_bn: [], popular: false, display_order: 0, active: true, popup_duration: 7, popup_active: false, delivery_days: "5-6 business days" },
    orderBy: { col: "display_order", ascending: true },
  },
  {
    table: "home_packages", label: "Home Packages", icon: CreditCard,
    listCols: ["name", "price", "popular", "display_order", "active"],
    fields: [
      { key: "name", label: "Name (English)", type: "text", required: true },
      { key: "name_bn", label: "Name (Bengali / বাংলা)", type: "text" },
      { key: "tagline", label: "Tagline (English)", type: "text" },
      { key: "tagline_bn", label: "Tagline (Bengali / বাংলা)", type: "text" },
      { key: "price", label: "Price (e.g. '৳ 1,500' or 'Quote')", type: "text", required: true },
      { key: "price_type", label: "Price type", type: "select", options: [
        { value: "amount", label: "Amount" },
        { value: "quote", label: "Quote / Custom" },
      ]},
      { key: "delivery", label: "Delivery time — English", type: "text" },
      { key: "delivery_bn", label: "Delivery time — Bengali / বাংলা", type: "text" },
      { key: "features", label: "Features — English (JSON array)", type: "json" },
      { key: "features_bn", label: "Features — Bengali / বাংলা (JSON array)", type: "json" },
      { key: "popular", label: "Most Popular (highlighted)", type: "boolean" },
      { key: "display_order", label: "Order", type: "number" },
      { key: "active", label: "Active", type: "boolean" },
    ],
    defaults: { features: [], features_bn: [], popular: false, price_type: "amount", display_order: 0, active: true },
    orderBy: { col: "display_order", ascending: true },
  },
  {
    table: "faq", label: "FAQ", icon: HelpCircle,
    listCols: ["question", "display_order", "active"],
    fields: [
      { key: "question", label: "Question", type: "text", required: true },
      { key: "answer", label: "Answer", type: "textarea", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "display_order", label: "Order", type: "number" },
      { key: "active", label: "Active", type: "boolean" },
    ],
    defaults: { display_order: 0, active: true },
    orderBy: { col: "display_order", ascending: true },
  },
  {
    table: "testimonials", label: "Testimonials", icon: MessageSquareQuote,
    listCols: ["name", "rating", "active"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "role", label: "Role", type: "text" },
      { key: "company", label: "Company", type: "text" },
      { key: "content", label: "Content", type: "textarea", required: true },
      { key: "rating", label: "Rating (1–5)", type: "number" },
      { key: "avatar_url", label: "Avatar URL", type: "text" },
      { key: "display_order", label: "Order", type: "number" },
      { key: "active", label: "Active", type: "boolean" },
    ],
    defaults: { rating: 5, display_order: 0, active: true },
    orderBy: { col: "display_order", ascending: true },
  },
  {
    table: "payment_settings", label: "Payment Methods", icon: CreditCard,
    listCols: ["method", "account_number", "active"],
    fields: [
      { key: "method", label: "Method (bKash/Nagad/Bank/...)", type: "text", required: true },
      { key: "account_name", label: "Account name (অ্যাকাউন্ট হোল্ডারের নাম)", type: "text" },
      { key: "account_number", label: "Account / number (অ্যাকাউন্ট / মোবাইল নম্বর)", type: "text", required: true },
      { key: "account_type", label: "Account type (Savings / Current / Personal)", type: "text" },
      { key: "bank_name", label: "Bank name (ব্যাংকের নাম — শুধু ব্যাংক পেমেন্টের জন্য)", type: "text" },
      { key: "branch_name", label: "Branch name (ব্রাঞ্চের নাম)", type: "text" },
      { key: "routing_number", label: "Routing number (রাউটিং নম্বর)", type: "text" },
      { key: "instructions", label: "Instructions", type: "textarea" },
      { key: "processing_fee", label: "Processing fee (BDT)", type: "number" },
      { key: "logo_url", label: "Logo (ক্রপ ও জুম করতে পারবেন)", type: "crop_image" },
      { key: "brand_color", label: "Brand color (hex — e.g. #D12053). Auto-detect হবে যদি ফাঁকা থাকে।", type: "text" },
      { key: "display_order", label: "Order", type: "number" },
      { key: "active", label: "Active", type: "boolean" },
    ],
    defaults: { display_order: 0, active: true, processing_fee: 0 },
    orderBy: { col: "display_order", ascending: true },
  },
  {
    table: "blog_posts", label: "Blog", icon: BookOpen,
    listCols: ["title", "slug", "published"],
    fields: [
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "title", label: "Title (English)", type: "text", required: true },
      { key: "title_bn", label: "Title (Bengali / বাংলা)", type: "text" },
      { key: "excerpt", label: "Excerpt (English)", type: "textarea" },
      { key: "excerpt_bn", label: "Excerpt (Bengali / বাংলা)", type: "textarea" },
      { key: "content", label: "Content — English (Markdown / HTML)", type: "textarea" },
      { key: "content_bn", label: "Content — Bengali / বাংলা (Markdown / HTML)", type: "textarea" },
      { key: "cover_url", label: "Cover Image (JPG/PNG আপলোড করুন)", type: "image" },
      { key: "author", label: "Author", type: "text" },
      { key: "tags", label: "Category Tags (select from SEO keywords)", type: "seo_multi" },
      { key: "seo_title", label: "SEO title (English)", type: "text" },
      { key: "seo_title_bn", label: "SEO title (Bengali / বাংলা)", type: "text" },
      { key: "seo_description", label: "SEO description (English)", type: "textarea" },
      { key: "seo_description_bn", label: "SEO description (Bengali / বাংলা)", type: "textarea" },
      { key: "published_at", label: "Publication Date & Time", type: "datetime" },
      { key: "published", label: "Published (live on site)", type: "boolean" },
    ],
    defaults: { tags: [], published: false },
    orderBy: { col: "published_at", ascending: false },
  },
  {
    table: "knowledge_hub", label: "Knowledge Hub (নলেজ হাব)", icon: BookOpen,
    listCols: ["title", "slug", "category", "status"],
    fields: [
      { key: "slug", label: "Slug (URL)", type: "text", required: true },
      { key: "title", label: "Title — বাংলা শিরোনাম", type: "text", required: true },
      { key: "title_en", label: "Title (English, optional)", type: "text" },
      { key: "meta_title", label: "Meta Title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta Description (SEO)", type: "textarea" },
      { key: "seo_keywords", label: "Target SEO Keywords", type: "tags" },
      { key: "category", label: "Category Tag (e.g. Income Tax, VAT Registration, NBR Rules)", type: "text" },
      { key: "cover_url", label: "Cover Image", type: "image" },
      { key: "content", label: "Content / Body (Markdown or HTML)", type: "textarea", required: true },
      { key: "published_at", label: "Publication Date (ISO, e.g. 2026-06-16)", type: "text" },
      { key: "status", label: "Status", type: "select", options: [
        { value: "draft", label: "Draft" },
        { value: "published", label: "Published" },
      ] },
    ],
    defaults: { status: "draft", seo_keywords: [] },
    orderBy: { col: "created_at", ascending: false },
  },
  {
    table: "contact_info", label: "Contact Info", icon: Phone, singleton: true,
    listCols: ["phone", "email"],
    fields: [
      { key: "phone", label: "Phone", type: "text" },
      { key: "whatsapp", label: "WhatsApp", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "address", label: "Address", type: "text" },
      { key: "hours", label: "Hours", type: "text" },
      { key: "facebook_url", label: "Facebook URL", type: "text" },
      { key: "youtube_url", label: "YouTube URL", type: "text" },
      { key: "linkedin_url", label: "LinkedIn URL", type: "text" },
      { key: "instagram_url", label: "Instagram URL", type: "text" },
      { key: "twitter_url", label: "X (Twitter) URL", type: "text" },
      { key: "tiktok_url", label: "TikTok URL", type: "text" },
      { key: "whatsapp_channel_url", label: "WhatsApp Channel URL", type: "text" },
      { key: "map_embed_url", label: "Map embed URL", type: "text" },
    ],
  },
  {
    table: "seo_settings", label: "SEO", icon: Search, singleton: true,
    listCols: ["site_title"],
    fields: [
      { key: "site_title", label: "Site title", type: "text", required: true },
      { key: "site_description", label: "Site description", type: "textarea" },
      { key: "keywords", label: "SEO Keywords (add as tags)", type: "tags" },
      { key: "og_image_url", label: "OG image URL", type: "text" },
      { key: "twitter_handle", label: "Twitter handle", type: "text" },
      { key: "google_analytics_id", label: "Google Analytics ID", type: "text" },
      { key: "google_site_verification", label: "Google verification", type: "text" },
    ],
  },
  {
    table: "site_settings", label: "Site", icon: Settings, singleton: true,
    listCols: ["site_name"],
    fields: [
      { key: "site_name", label: "Site name", type: "text", required: true },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "logo_url", label: "Logo — Light mode (লাইট থিমের জন্য, JPG/PNG)", type: "image" },
      { key: "logo_url_dark", label: "Logo — Dark mode (ডার্ক থিমের জন্য, JPG/PNG)", type: "image" },
      { key: "favicon_url", label: "Favicon (image)", type: "image" },
      { key: "primary_color", label: "Primary color (hex)", type: "text" },
      { key: "footer_logo_url", label: "Footer Logo (ফুটার লোগো, JPG/PNG)", type: "image" },
      { key: "footer_about", label: "Footer about (ফুটার বিবরণ)", type: "textarea" },
      { key: "footer_text", label: "Footer copyright text (কপিরাইট লেখা)", type: "text" },
      { key: "font_weight", label: "Font weight (পুরো সাইটের ফন্ট ওজন)", type: "select", options: [
        { value: "light", label: "Light (পাতলা)" },
        { value: "normal", label: "Normal (সাধারণ)" },
        { value: "medium", label: "Medium (মাঝারি)" },
        { value: "semibold", label: "Semibold (একটু বোল্ড)" },
        { value: "bold", label: "Bold (বোল্ড)" },
      ]},
      { key: "font_size_scale", label: "Font size (পুরো সাইটের ফন্ট সাইজ)", type: "select", options: [
        { value: "small", label: "Small (ছোট)" },
        { value: "medium", label: "Medium (সাধারণ)" },
        { value: "large", label: "Large (বড়)" },
        { value: "xlarge", label: "Extra Large (অনেক বড়)" },
      ]},
      { key: "announcement_text", label: "Announcement", type: "text" },
      { key: "announcement_active", label: "Announcement active", type: "boolean" },
      { key: "tax_payment_url", label: "Tax Payment Link (ট্যাক্স পেমেন্ট লিংক)", type: "text" },
      { key: "vat_payment_url", label: "VAT Payment Link (ভ্যাট পেমেন্ট লিংক)", type: "text" },
      { key: "payment_widget_active", label: "Show Payment Widget on Home (হোমপেজে পেমেন্ট উইজেট দেখাও)", type: "boolean" },
      { key: "visa_tax_popup_active", label: "Visa Tax Popup — Show (ভিসা ট্যাক্স পেইজে পপআপ দেখাও)", type: "boolean" },
      { key: "visa_tax_popup_title", label: "Visa Tax Popup — Title", type: "text" },
      { key: "visa_tax_popup_message", label: "Visa Tax Popup — Message", type: "textarea" },
      { key: "visa_tax_popup_image_url", label: "Visa Tax Popup — Image (optional)", type: "image" },
      { key: "visa_tax_popup_cta_text", label: "Visa Tax Popup — Button text (optional)", type: "text" },
      { key: "visa_tax_popup_cta_link", label: "Visa Tax Popup — Button link (optional)", type: "text" },
      { key: "signup_prompt_active", label: "Signup Prompt — Show to logged-out visitors (লগইন ছাড়া ভিজিটরদের সাইন আপ পপআপ দেখাও)", type: "boolean" },
      { key: "signup_prompt_delay_seconds", label: "Signup Prompt — Delay before showing (seconds) — যত সেকেন্ড পর দেখাবে", type: "number" },
      { key: "signup_prompt_auto_close_seconds", label: "Signup Prompt — Auto close after (seconds, 0 = manual close only) — কত সেকেন্ড পর নিজে থেকে বন্ধ হবে", type: "number" },
      { key: "signup_prompt_title", label: "Signup Prompt — Title", type: "text" },
      { key: "signup_prompt_message", label: "Signup Prompt — Message", type: "textarea" },
      { key: "signup_prompt_cta_text", label: "Signup Prompt — Button text", type: "text" },
      { key: "home_seo1_heading_en", label: "Home SEO Block 1 — Heading (English)", type: "text" },
      { key: "home_seo1_heading_bn", label: "Home SEO Block 1 — Heading (Bangla / বাংলা)", type: "text" },
      { key: "home_seo1_body_en", label: "Home SEO Block 1 — Body (English, HTML allowed, paragraphs separated by blank line)", type: "textarea" },
      { key: "home_seo1_body_bn", label: "Home SEO Block 1 — Body (Bangla / বাংলা, HTML allowed, paragraphs separated by blank line)", type: "textarea" },
      { key: "home_seo2_heading_en", label: "Home SEO Block 2 — Heading (English)", type: "text" },
      { key: "home_seo2_heading_bn", label: "Home SEO Block 2 — Heading (Bangla / বাংলা)", type: "text" },
      { key: "home_seo2_body_en", label: "Home SEO Block 2 — Body (English, HTML allowed, paragraphs separated by blank line)", type: "textarea" },
      { key: "home_seo2_body_bn", label: "Home SEO Block 2 — Body (Bangla / বাংলা, HTML allowed, paragraphs separated by blank line)", type: "textarea" },
      
      
    ],
  },
  {
    table: "whatsapp_numbers", label: "WhatsApp", icon: MessageCircle,
    listCols: ["label", "number", "department", "active"],
    fields: [
      { key: "label", label: "Label", type: "text", required: true },
      { key: "number", label: "Number (with country code)", type: "text", required: true },
      { key: "department", label: "Department", type: "text" },
      { key: "default_message", label: "Default message", type: "textarea" },
      { key: "display_order", label: "Order", type: "number" },
      { key: "active", label: "Active", type: "boolean" },
    ],
    defaults: { display_order: 0, active: true },
    orderBy: { col: "display_order", ascending: true },
  },
  {
    table: "popup_offers", label: "Popup Offers", icon: Megaphone,
    listCols: ["title", "active", "starts_at", "ends_at"],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "message", label: "Message", type: "textarea" },
      { key: "image_url", label: "Image (PNG/JPG)", type: "image" },
      { key: "cta_text", label: "CTA text", type: "text" },
      { key: "cta_link", label: "CTA link", type: "text" },
      { key: "service_code", label: "Service code (optional — click popup goes to this service)", type: "text" },
      { key: "starts_at", label: "Starts at (ISO date)", type: "text" },
      { key: "ends_at", label: "Ends at (ISO date)", type: "text" },
      { key: "delay_seconds", label: "Delay before showing (seconds)", type: "number" },
      { key: "auto_close_seconds", label: "Auto-close after (seconds, 0 = manual close only)", type: "number" },
      { key: "background_color", label: "Popup background color (only this popup box)", type: "select", options: [
        { value: "", label: "Default (theme)" },
        { value: "#ffffff", label: "White" },
        { value: "#fef3c7", label: "Yellow" },
        { value: "#dcfce7", label: "Light Green" },
        { value: "#dbeafe", label: "Light Blue" },
        { value: "#fce7f3", label: "Pink" },
        { value: "#f3e8ff", label: "Lavender" },
        { value: "#ffedd5", label: "Peach" },
        { value: "#fee2e2", label: "Light Red" },
        { value: "#111827", label: "Dark" },
      ] },
      { key: "display_mode", label: "Display style", type: "select", options: [
        { value: "modal", label: "Center modal (full-screen overlay)" },
        { value: "floating", label: "Floating card (bottom-left, homepage only)" },
      ] },
      { key: "display_order", label: "Order", type: "number" },
      { key: "active", label: "Active (toggle ON to show)", type: "boolean" },
    ],
    defaults: { display_order: 0, active: true, delay_seconds: 1, auto_close_seconds: 0, display_mode: "modal" },
    orderBy: { col: "display_order", ascending: true },
  },
  {
    table: "visa_tax_packages", label: "Visa Tax", icon: Plane,
    listCols: ["name", "duration_years", "price", "popular", "active"],
    fields: [
      { key: "name", label: "Package Name", type: "text", required: true },
      { key: "bn_name", label: "Bangla Name (বাংলা নাম)", type: "text" },
      { key: "duration_years", label: "Duration (years)", type: "number", required: true },
      { key: "price", label: "Price (BDT)", type: "number", required: true },
      { key: "description", label: "Description (English)", type: "textarea" },
      { key: "description_bn", label: "Description (Bengali / বাংলা)", type: "textarea" },
      { key: "features", label: "Features — English (JSON array)", type: "json" },
      { key: "features_bn", label: "Features — Bengali / বাংলা (JSON array)", type: "json" },
      { key: "document_checklist", label: "Document Checklist — English (JSON array — required docs for invoice)", type: "json" },
      { key: "document_checklist_bn", label: "Document Checklist — Bengali / বাংলা (JSON array)", type: "json" },
      { key: "delivery_days", label: "Delivery time — English (e.g. '5-6 business days')", type: "text" },
      { key: "delivery_days_bn", label: "Delivery time — Bengali / বাংলা", type: "text" },
      { key: "pay_url", label: "Pay Now Link (পেমেন্ট লিংক)", type: "text" },
      { key: "whatsapp_number", label: "WhatsApp Number (with country code, e.g. 8801XXXXXXXXX)", type: "text" },
      { key: "popular", label: "Most Popular (only one should be selected)", type: "boolean" },
      { key: "active", label: "Active (show on site)", type: "boolean" },
      { key: "display_order", label: "Order", type: "number" },
    ],
    defaults: { duration_years: 1, price: 0, currency: "BDT", features: [], features_bn: [], document_checklist: [], document_checklist_bn: [], delivery_days: "5-6 business days", popular: false, active: true, display_order: 0 },
    orderBy: { col: "display_order", ascending: true },
  },
];

const ORDER_STATUSES = ["pending", "processing", "completed", "rejected"] as const;
const PAYMENT_STATUSES = ["pending_verification", "verified", "rejected"] as const;

function statusColor(s: string) {
  switch (s) {
    case "completed":
    case "verified":
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
    case "processing":
      return "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30";
    case "rejected":
      return "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30";
    default:
      return "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30";
  }
}

function AdminLoginInline() {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await signInAdminByPassword({ data: { password } });
      if (!res.ok) { toast.error(res.error); return; }
      const { error } = await supabase.auth.setSession({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
      });
      if (error) { toast.error(t("admin.login.failed")); return; }
      try {
        // Lifetime admin session: never ask to log in again.
        localStorage.setItem("otv_remember_until", String(Date.now() + 100 * 365 * 24 * 3600 * 1000));
        sessionStorage.removeItem("otv_session_only");
      } catch {}
      toast.success(t("admin.login.welcome"));
      location.reload();
    } catch (err: any) {
      toast.error(err?.message ?? t("admin.login.failed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <SiteShell>
      <section className="pt-14 md:pt-20 pb-20">
        <div className="container-page max-w-md">
          <div className="glass-strong rounded-3xl p-8 shadow-glow">
            <div className="text-center"><Logo className="justify-center" /></div>
            <h1 className="mt-6 font-display font-bold text-2xl text-center">{t("admin.login.title")}</h1>
            <p className="text-sm text-muted-foreground text-center mt-1">শুধুমাত্র admin dashboard access</p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="admin-password">{t("admin.login.password")}</Label>
                <div className="relative mt-1.5">
                  <Input id="admin-password" type={showPassword ? "text" : "password"} required value={password}
                    placeholder={t("admin.login.passwordPlaceholder")}
                    onChange={(e) => setPassword(e.target.value)} className="pr-10" autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full gradient-primary text-primary-foreground shadow-glow">
                {loading ? "Signing in…" : t("admin.login.submit")}
              </Button>
            </form>
            <p className="mt-6 text-xs text-center text-muted-foreground">
              User? <Link to="/login" className="text-primary font-semibold hover:underline">User login</Link>
            </p>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

const MODERATOR_ALLOWED = new Set(["overview", "orders", "payment_settings", "delivery", "users", "analytics"]);

function AdminPage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState<"admin" | "moderator" | null | undefined>(undefined);
  const [section, setSection] = useState<string>("overview");
  const claimAdmin = useServerFn(claimFirstAdmin);
  const getRole = useServerFn(getMyAdminRole);
  const isAdmin = role === "admin";
  const isModerator = role === "moderator";
  const hasAccess = isAdmin || isModerator;
  const currentLabel = TABLE_LABELS[section] ?? TABLES.find((x) => x.table === section)?.label ?? "Admin";

  const modIdentity = useModeratorIdentity(isModerator);
  const needsModName = isModerator && !modIdentity.name;
  useAdminPresence(hasAccess, modIdentity.name);


  useEffect(() => {
    if (authLoading) return;
    if (!user) { setRole(null); return; }
    (async () => {
      try {
        const res = await getRole({});
        setRole((res.role as any) ?? null);
      } catch {
        setRole(null);
      }
    })();
  }, [user, authLoading, getRole]);

  // Moderator hard guard: kick back to overview if they navigate to a forbidden section.
  useEffect(() => {
    if (isModerator && !MODERATOR_ALLOWED.has(section)) {
      setSection("overview");
    }
  }, [isModerator, section]);

  const navGroups = useMemo(() => {
    if (isAdmin) {
      // Inject Admin Active under Operations
      return ADMIN_NAV_GROUPS.map((g) =>
        g.label === "Operations"
          ? { ...g, items: [...g.items, { id: "admin_active", label: "Admin Active", icon: ShieldCheck }] }
          : g,
      );
    }
    if (isModerator) {
      return [{
        label: "Moderator",
        items: [
          { id: "overview", label: "Dashboard", icon: BarChart3 },
          { id: "orders", label: "Orders", icon: ShoppingCart },
          { id: "payment_settings", label: "Payment", icon: CreditCard },
          { id: "delivery", label: "Delivery", icon: Truck },
          { id: "users", label: "Users", icon: Users },
          { id: "analytics", label: "Overview", icon: BarChart3 },
        ],
      }];
    }
    return [];
  }, [isAdmin, isModerator]);

  async function onClaimAdmin() {
    const res = await claimAdmin({});
    if (res.promoted) { toast.success(t("admin.claim.promoted")); setTimeout(() => location.reload(), 600); }
    else toast.error(t("admin.claim.exists"));
  }

  if (authLoading || role === undefined) {
    return <SiteShell><div className="container-page py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></SiteShell>;
  }
  if (!user) {
    return <AdminLoginInline />;
  }
  if (!hasAccess) {
    return (
      <SiteShell>
        <div className="container-page py-24 max-w-lg">
          <div className="glass-strong rounded-3xl p-8 text-center shadow-glow">
            <ShieldCheck className="h-12 w-12 text-primary mx-auto" />
            <h1 className="mt-4 font-display text-2xl font-bold">{t("admin.claim.title")}</h1>
            <p className="mt-2 text-muted-foreground">এই account-এ admin access নেই। No admin exists yet? Claim it.</p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              <Button onClick={onClaimAdmin} className="gradient-primary text-primary-foreground">{t("admin.claim.claimFirst")}</Button>
              <Button variant="outline" onClick={async () => { await supabase.auth.signOut(); location.reload(); }}>{t("admin.claim.signOut")}</Button>
              <Button variant="outline" onClick={() => navigate({ to: "/" })}>{t("admin.claim.home")}</Button>
            </div>
          </div>
        </div>
      </SiteShell>
    );
  }

  return (
    <div className="admin-surface min-h-screen w-full">
      <ModeratorNameGate open={needsModName} onSubmit={(n) => modIdentity.save(n)} />
      <NewOrderNotifier enabled={isAdmin} onViewOrders={() => setSection("orders")} />
      <AdminTopNav
        groups={navGroups}
        active={section}
        onSelect={(id) => {
          if (isModerator && !MODERATOR_ALLOWED.has(id)) return;
          setSection(id);
        }}
        currentLabel={currentLabel}
      />
      {isModerator && modIdentity.name && (
        <div className="px-4 md:px-6 lg:px-8 pt-2">
          <div className="glass rounded-lg px-3 py-2 text-xs flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-semibold text-primary">Moderator:</span>
            <span className="font-mono">{modIdentity.name}</span>
            <span className="text-muted-foreground">
              Logged in: {modIdentity.loginTime ? new Date(modIdentity.loginTime).toLocaleString() : "—"}
            </span>
          </div>
        </div>
      )}
      <main className="p-4 md:p-6 lg:p-8 overflow-x-hidden">
        {needsModName ? (
          <div className="container-page py-24 text-center text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            <p className="mt-3 text-sm">নাম দেওয়ার পর প্যানেল চালু হবে…</p>
          </div>
        ) : isModerator && !MODERATOR_ALLOWED.has(section) ? null : (

        <Tabs value={section} onValueChange={(v) => {
          if (isModerator && !MODERATOR_ALLOWED.has(v)) return;
          setSection(v);
        }} className="w-full">
          <TabsList className="sr-only">
            <TabsTrigger value="admin_active">admin active</TabsTrigger>
            <TabsTrigger value="web_marketing">web marketing</TabsTrigger>
            <TabsTrigger value="overview">overview</TabsTrigger>
            <TabsTrigger value="analytics">analytics</TabsTrigger>
            <TabsTrigger value="revenue">revenue</TabsTrigger>
            <TabsTrigger value="profit">profit</TabsTrigger>
            <TabsTrigger value="orders">orders</TabsTrigger>
            <TabsTrigger value="delivery">delivery</TabsTrigger>
            <TabsTrigger value="users">users</TabsTrigger>
            <TabsTrigger value="order_notify">order_notify</TabsTrigger>
            <TabsTrigger value="push_sms">push & sms</TabsTrigger>
            <TabsTrigger value="b2b">b2b</TabsTrigger>
            <TabsTrigger value="b2b_submissions">b2b submissions</TabsTrigger>
            <TabsTrigger value="referrals">referrals</TabsTrigger>
            <TabsTrigger value="commission_requests">commission requests</TabsTrigger>
            <TabsTrigger value="invoice">invoice</TabsTrigger>
            <TabsTrigger value="seo_health">seo_health</TabsTrigger>
            <TabsTrigger value="seo_checklist">seo_checklist</TabsTrigger>
            <TabsTrigger value="site_keywords">🔑 site keywords</TabsTrigger>
            <TabsTrigger value="directory_submit">directory_submit</TabsTrigger>
            {TABLES.map((tbl) => (<TabsTrigger key={tbl.table} value={tbl.table}>{tbl.label}</TabsTrigger>))}
            <TabsTrigger value="watermark_icons">watermark icons</TabsTrigger>
            <TabsTrigger value="backup">backup</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0"><AdminOverview onJump={setSection} /></TabsContent>
          <TabsContent value="analytics" className="mt-0"><AnalyticsPanel /></TabsContent>
          <TabsContent value="revenue" className="mt-0"><RevenuePanel /></TabsContent>
          <TabsContent value="profit" className="mt-0"><ProfitPanel /></TabsContent>
          <TabsContent value="orders" className="mt-0"><OrdersPanel /></TabsContent>
          <TabsContent value="delivery" className="mt-0"><DeliveryPanel /></TabsContent>
          <TabsContent value="users" className="mt-0"><UsersPanel /></TabsContent>
          <TabsContent value="order_notify" className="mt-0"><OrderNotifyPanel /></TabsContent>
          <TabsContent value="push_sms" className="mt-0"><PushSmsPanel /></TabsContent>
          <TabsContent value="b2b" className="mt-0"><B2BPricingPanel /></TabsContent>
          <TabsContent value="b2b_submissions" className="mt-0"><B2BSubmissions /></TabsContent>
          <TabsContent value="referrals" className="mt-0"><ReferralManagement /></TabsContent>
          <TabsContent value="commission_requests" className="mt-0">{isAdmin && <CommissionRequestsPanel />}</TabsContent>
          <TabsContent value="invoice" className="mt-0"><InvoicePanel /></TabsContent>
          <TabsContent value="seo_health" className="mt-0"><SeoHealthPanel /></TabsContent>
          <TabsContent value="seo_checklist" className="mt-0"><SeoChecklistPanel /></TabsContent>
          <TabsContent value="site_keywords" className="mt-0">{isAdmin && <SiteKeywordsPanel />}</TabsContent>
          <TabsContent value="directory_submit" className="mt-0"><DirectorySubmitPanel /></TabsContent>
          <TabsContent value="admin_active" className="mt-0">{isAdmin && <AdminActive />}</TabsContent>
          <TabsContent value="web_marketing" className="mt-0">{isAdmin && <WebMarketingPanel />}</TabsContent>
          <TabsContent value="watermark_icons" className="mt-0">{isAdmin && <WatermarkIconsPanel />}</TabsContent>
          {TABLES.map((tbl) => (
            <TabsContent key={tbl.table} value={tbl.table} className="mt-0">
              {tbl.table === "home_packages" ? <HomePackagesWrapper config={tbl} /> : <CrudPanel config={tbl} />}
              {tbl.table === "seo_settings" && <LiveRankingDashboard />}
            </TabsContent>
          ))}
          <TabsContent value="backup" className="mt-0"><BackupPanel /></TabsContent>
        </Tabs>
        )}
      </main>
    </div>
  );
}

// Sidebar nav configuration
const TABLE_LABELS: Record<string, string> = {
  overview: "Overview", analytics: "Analytics", revenue: "Revenue", profit: "Profit Tracker", orders: "Orders",
  delivery: "Delivery", users: "Users", order_notify: "Order Notifications",
  b2b: "B2B Pricing", referrals: "রেফার (Referral)", invoice: "Invoice Template", backup: "Backup & Restore",
  seo_health: "SEO Health", seo_checklist: "SEO Checklist", directory_submit: "Submit to Directories", watermark_icons: "Watermark Icons",
};
const ADMIN_NAV_GROUPS = [
  { label: "Dashboard", items: [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "campaign_settings", label: "🎉 Campaign Settings", icon: Megaphone },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "revenue", label: "Revenue", icon: CreditCard },
    { id: "profit", label: "Profit Tracker", icon: BarChart3 },
  ]},
  { label: "Operations", items: [
    { id: "orders", label: "Orders", icon: ShoppingCart },
    { id: "delivery", label: "Delivery", icon: Truck },
    { id: "users", label: "Users", icon: Users },
    { id: "order_notify", label: "Order Notify", icon: MessageCircle },
    { id: "web_marketing", label: "Web Marketing", icon: Megaphone },
    { id: "referrals", label: "রেফার", icon: ShieldCheck },
    { id: "b2b_submissions", label: "B2B Customer Submissions", icon: Briefcase },
    { id: "commission_requests", label: "💸 Commission Requests", icon: Wallet },
  ]},
  { label: "Content", items: [
    { id: "hero_section", label: "Hero", icon: ImageIcon },
    { id: "services", label: "Services", icon: FileText },
    { id: "packages", label: "Packages", icon: CreditCard },
    { id: "home_packages", label: "Home Packages", icon: CreditCard },
    { id: "visa_tax_packages", label: "Visa Tax", icon: Plane },
    { id: "blog_posts", label: "Blog", icon: BookOpen },
    { id: "knowledge_hub", label: "Knowledge Hub (নলেজ হাব)", icon: BookOpen },
    { id: "faq", label: "FAQ", icon: HelpCircle },
    { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
    { id: "popup_offers", label: "Popup Offers", icon: Megaphone },
  ]},
  { label: "Settings", items: [
    { id: "site_settings", label: "Site", icon: Settings },
    { id: "seo_settings", label: "SEO", icon: Search },
    { id: "seo_health", label: "SEO Health", icon: Search },
    { id: "seo_checklist", label: "SEO Checklist", icon: Search },
    { id: "directory_submit", label: "Submit to Directories", icon: ExternalLink },
    { id: "contact_info", label: "Contact", icon: Phone },
    { id: "payment_settings", label: "Payments", icon: CreditCard },
    { id: "whatsapp_numbers", label: "WhatsApp", icon: MessageCircle },
  ]},
  { label: "Tools", items: [
    { id: "b2b", label: "B2B Pricing", icon: ShieldCheck },
    { id: "invoice", label: "Invoice Template", icon: Receipt },
    { id: "watermark_icons", label: "Watermark Icons", icon: Sparkles },
    { id: "backup", label: "Backup", icon: Database },
  ]},
];


// ---------- WhatsApp Order Notification ----------
function OrderNotifyPanel() {
  const [rowId, setRowId] = useState<string | null>(null);
  const [number, setNumber] = useState("");
  const [apikey, setApikey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const getSettings = useServerFn(adminGetOrderNotifySettings);
  const saveSettings = useServerFn(adminSaveOrderNotifySettings);

  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings({});
        setRowId(data.id);
        setNumber(data.whatsapp || "");
        setApikey(data.apikey || "");
      } catch (e: any) {
        toast.error(e?.message ?? "Load failed");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function save() {
    const cleaned = number.replace(/[^\d]/g, "");
    if (cleaned.length < 8) {
      toast.error("সঠিক WhatsApp নাম্বার দিন (country code সহ)");
      return;
    }
    setSaving(true);
    try {
      const res = await saveSettings({ data: { id: rowId, whatsapp: cleaned, apikey: apikey.trim() || null } });
      setRowId(res.id);
      setNumber(cleaned);
      toast.success("সংরক্ষিত হয়েছে");
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="glass rounded-2xl p-6 max-w-2xl">
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle className="h-5 w-5 text-emerald-600" />
        <h2 className="text-xl font-semibold">হোয়াটসঅ্যাপ নোটিফিকেশন</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        নতুন অর্ডার এলে সয়ংক্রিয়ভাবে আপনার WhatsApp-এ মেসেজ যাবে। শুধু আপনি (অ্যাডমিন) এটি দেখতে পাবেন।
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> লোড হচ্ছে…</div>
      ) : (
        <div className="space-y-5">
          <div>
            <Label htmlFor="order_notify_whatsapp">অর্ডার নোটিফিকেশন — WhatsApp Number</Label>
            <Input
              id="order_notify_whatsapp"
              inputMode="numeric"
              placeholder="8801XXXXXXXXX"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              className="mt-1.5"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Country code সহ লিখুন (যেমন <span className="font-mono">8801XXXXXXXXX</span>)। কোনো +, স্পেস বা ড্যাশ লাগবে না।
            </p>
          </div>

          <div>
            <Label htmlFor="callmebot_apikey">CallMeBot API Key</Label>
            <Input
              id="callmebot_apikey"
              placeholder="123456"
              value={apikey}
              onChange={(e) => setApikey(e.target.value)}
              className="mt-1.5 font-mono"
            />
            <div className="text-xs text-muted-foreground mt-2 space-y-1 bg-muted/40 rounded-lg p-3">
              <p className="font-semibold text-foreground">⚙️ এক-বার সেটআপ (১ মিনিট, সম্পূর্ণ ফ্রি):</p>
              <p>১. আপনার WhatsApp-এ <span className="font-mono">+34 644 51 95 23</span> নাম্বারটি সেভ করুন (নাম: CallMeBot)।</p>
              <p>২. ঐ নাম্বারে মেসেজ পাঠান: <span className="font-mono bg-background px-1.5 py-0.5 rounded">I allow callmebot to send me messages</span></p>
              <p>৩. কিছুক্ষণের মধ্যে আপনি একটি reply পাবেন যেখানে আপনার <b>API Key</b> দেওয়া থাকবে (যেমন <span className="font-mono">"Your APIKEY is 1234567"</span>)।</p>
              <p>৪. সেই key এখানে বসিয়ে <b>সংরক্ষণ</b> করুন। ব্যস! এরপর প্রতিটি নতুন অর্ডারে সয়ংক্রিয়ভাবে WhatsApp মেসেজ পাবেন।</p>
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="gradient-primary text-primary-foreground">
            {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> সংরক্ষণ হচ্ছে…</> : "সংরক্ষণ করুন"}
          </Button>
        </div>
      )}
    </div>
  );
}

function PushSmsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tpl, setTpl] = useState("");
  const [rejectOnly, setRejectOnly] = useState(true);
  const getPushSms = useServerFn(adminGetPushSmsSettings);
  const savePushSms = useServerFn(adminSavePushSmsSettings);

  useEffect(() => {
    (async () => {
      try {
        const data = await getPushSms({});
        setTpl(data.template);
        setRejectOnly(data.onlyRejections);
      } catch {}
      setLoading(false);
    })();
  }, [getPushSms]);

  async function save() {
    setSaving(true);
    try {
      await savePushSms({ data: { template: tpl, onlyRejections: rejectOnly } });
      toast.success("সংরক্ষিত");
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="glass rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-1">📱 ফোন বন্ধ থাকলেও Push Notification</h2>
        <p className="text-sm text-muted-foreground">
          User যখন প্রথম login করে notification permission দেয়, তখন তার ফোন/browser-এ সয়ংক্রিয়ভাবে Web Push subscription save হয়।
          এরপর order/payment reject বা ready হলে, website বন্ধ থাকলেও তার ফোনে notification পৌঁছাবে। VAPID keys ও service worker
          সেট আপ করা আছে — আলাদা কিছু করার দরকার নেই।
        </p>
      </div>

      <div className="glass rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-1">✉️ Payment Rejected হলে SMS</h2>
        <p className="text-sm text-muted-foreground mb-4">
          আপনার SMS gateway-এর URL template নিচে দিন। <code className="font-mono">{"{phone}"}</code> এবং{" "}
          <code className="font-mono">{"{message}"}</code> placeholder ব্যবহার করুন। User-এর profile-এর phone নাম্বারে SMS যাবে।
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> লোড হচ্ছে…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="sms_tpl">SMS Gateway URL Template</Label>
              <Input
                id="sms_tpl"
                placeholder="https://bulksmsbd.net/api/smsapi?api_key=XXX&type=text&number={phone}&senderid=YYY&message={message}"
                value={tpl}
                onChange={(e) => setTpl(e.target.value)}
                className="mt-1.5 font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                Example providers: BulkSMSBD, SSL Wireless, MIM SMS — সবার simple HTTP GET API আছে।
                Empty রাখলে SMS পাঠানো হবে না (শুধু in-app + push)।
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="reject_only"
                type="checkbox"
                checked={rejectOnly}
                onChange={(e) => setRejectOnly(e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="reject_only" className="cursor-pointer">
                শুধু reject (order/payment বাতিল) notification-এ SMS পাঠাও
              </Label>
            </div>
            <Button onClick={save} disabled={saving} className="gradient-primary text-primary-foreground">
              {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> সংরক্ষণ…</> : "সংরক্ষণ করুন"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}



function PortalCredentialsBlock({ order }: { order: any }) {
  const [show, setShow] = useState(false);
  const uid = (order.portal_user_id ?? "").toString();
  const pw = (order.portal_password ?? "").toString();
  if (!uid && !pw) return null;
  const label = order.portal_label || "Associated Portal";
  const copy = async (val: string, what: string) => {
    if (!val) return;
    try { await navigator.clipboard.writeText(val); toast.success(`${what} copied`); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="mt-2 rounded-md border border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-950/30 p-2">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
        <KeyRound className="h-3 w-3" /> Associated Portal Login Credentials
      </div>
      <div className="mt-0.5 text-[10px] text-emerald-700/80 dark:text-emerald-300/80">{label}</div>
      {uid && (
        <div className="mt-1.5 flex items-center gap-1">
          <span className="text-[10px] text-muted-foreground w-10 shrink-0">User</span>
          <code className="flex-1 min-w-0 truncate rounded bg-background/70 px-1.5 py-0.5 text-[11px] font-mono">{uid}</code>
          <button onClick={() => copy(uid, "User ID")} className="h-6 w-6 rounded hover:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 flex items-center justify-center" title="Copy User ID">
            <Copy className="h-3 w-3" />
          </button>
        </div>
      )}
      {pw && (
        <div className="mt-1 flex items-center gap-1">
          <span className="text-[10px] text-muted-foreground w-10 shrink-0">Pass</span>
          <code className="flex-1 min-w-0 truncate rounded bg-background/70 px-1.5 py-0.5 text-[11px] font-mono">
            {show ? pw : "•".repeat(Math.min(pw.length, 10))}
          </code>
          <button onClick={() => setShow((v) => !v)} className="h-6 w-6 rounded hover:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 flex items-center justify-center" title={show ? "Hide" : "Show"}>
            {show ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          </button>
          <button onClick={() => copy(pw, "Password")} className="h-6 w-6 rounded hover:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 flex items-center justify-center" title="Copy Password">
            <Copy className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Orders ----------
function OrdersPanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [b2bIds, setB2bIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [resettingOrderId, setResettingOrderId] = useState<string | null>(null);
  const updateOrder = useServerFn(adminUpdateOrder);
  async function load() {
    setLoading(true);
    const [{ data, error }, { data: roles }] = await Promise.all([
      supabase.from("orders").select("*").order("created_at", { ascending: false }),
      supabase.from("user_roles").select("user_id, role").eq("role", "b2b" as any),
    ]);
    if (error) toast.error(error.message); else setRows(data ?? []);
    setB2bIds(new Set((roles ?? []).map((r: any) => r.user_id)));
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  // Realtime: instantly refresh orders when any change happens (insert/update/delete)
  useEffect(() => {
    const channel = supabase
      .channel("admin-orders-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "orders" }, () => {
        load();
      })
      .subscribe();
    // Polling fallback every 15s in case realtime is unavailable
    const poll = setInterval(() => { load(); }, 15000);
    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, []);

  // Listen for jump-to-order events dispatched by the Pending Payments widget
  useEffect(() => {
    function onFocus(e: any) {
      const oid = e?.detail?.orderId as string | undefined;
      if (!oid) return;
      const target = rows.find((r) => r.id === oid);
      if (target?.reference_id) {
        setSearch(target.reference_id);
        setTimeout(() => {
          document.getElementById(`order-row-${oid}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      } else {
        setSearch(oid.slice(0, 8));
      }
    }
    window.addEventListener("admin:focus-order", onFocus as any);
    return () => window.removeEventListener("admin:focus-order", onFocus as any);
  }, [rows]);

  async function updateField(id: string, patch: any) {
    try {
      await updateOrder({ data: { orderId: id, patch } });
      toast.success("Updated");
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    } catch (e: any) {
      toast.error(e?.message ?? "Update failed");
    }
  }
  const archiveOrderFn = useServerFn(adminArchiveOrder);
  const hardDeleteFn = useServerFn(adminHardDeleteOrder);
  async function archiveOrder(r: any) {
    const hasDocs = Array.isArray(r.document_urls) && r.document_urls.length > 0;
    const msg = hasDocs
      ? "Archive this order?\n\nWe strongly recommend downloading the asset backup FIRST. This will:\n• Remove uploaded files from storage to free space\n• Keep the order row, revenue, and customer history intact\n\nProceed?"
      : "Archive this order? Revenue and history stay intact; the row is marked archived.";
    if (!confirm(msg)) return;
    try {
      const res = await archiveOrderFn({ data: { orderId: r.id } });
      toast.success(`Archived — ${res.removed ?? 0} file(s) removed from storage`);
      setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, archived_at: new Date().toISOString(), document_urls: [], payment_screenshot_url: null } : x));
    } catch (e: any) {
      toast.error(e?.message ?? "Archive failed");
    }
  }
  async function hardDeleteOrder(r: any) {
    const amt = Number(r.total_price ?? r.amount ?? 0);
    const msg =
      `PERMANENTLY delete order ${r.reference_id || r.id.slice(0, 8)}?\n\n` +
      `This removes the row, all files, AND subtracts ৳${amt.toLocaleString()} from total revenue.\n\n` +
      `Use ONLY for test/fake orders. This cannot be undone.\n\nType-confirm by clicking OK.`;
    if (!confirm(msg)) return;
    if (!confirm("Final confirmation: permanently delete this order?")) return;
    try {
      const res = await hardDeleteFn({ data: { orderId: r.id } });
      toast.success(`Permanently deleted — ${res.removedFiles} file(s) purged`);
      setRows((rs) => rs.filter((x) => x.id !== r.id));
    } catch (e: any) {
      toast.error(e?.message ?? "Delete failed");
    }
  }


  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) => {
        const fields = [r.reference_id, r.customer_name, r.customer_email, r.customer_phone, r.service_title, r.package_name, r.status, r.payment_status]
          .filter(Boolean).map((v: any) => String(v).toLowerCase());
        return fields.some((f) => f.includes(q));
      })
    : rows;

  const searched = filtered.find((r) => String(r.reference_id).toLowerCase() === q);

  return (
    <div className="glass-strong rounded-2xl overflow-hidden no-hover-lift">
      <div className="flex flex-wrap justify-between items-center gap-3 p-4">
        <h3 className="font-display font-bold">Orders ({filtered.length}{filtered.length !== rows.length ? ` / ${rows.length}` : ""})</h3>
        <div className="flex items-center gap-2 flex-1 max-w-md ml-auto">
          <Input
            placeholder="অর্ডার নাম্বার / নাম / ফোন দিয়ে সার্চ করুন (যেমন OTV-XXXXXX)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9"
          />
          {search && <Button variant="ghost" size="sm" onClick={() => setSearch("")}>Clear</Button>}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}</Button>
        </div>
      </div>

      {searched && (
        <div className="mx-4 mb-4 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-mono text-xs px-2 py-0.5 rounded bg-background">{searched.reference_id}</span>
            <Badge variant="outline" className={statusColor(searched.status)}>Order: {searched.status}</Badge>
            <Badge variant="outline" className={statusColor(searched.payment_status)}>Payment: {searched.payment_status}</Badge>
            <Badge variant="outline">Delivery: {searched.delivery_status ?? "not_started"}</Badge>
            <span className="text-muted-foreground">{searched.customer_name} · {searched.customer_phone}</span>
            {b2bIds.has(searched.user_id) && <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-[10px]">B2B</Badge>}
            <span className="ml-auto font-semibold">{searched.service_title} — {searched.package_name}{searched.amount ? ` · ৳${searched.amount}` : ""}</span>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Ref</TableHead><TableHead>Service / Package</TableHead><TableHead>Customer</TableHead>
            <TableHead>Docs</TableHead><TableHead>Payment</TableHead><TableHead>Amounts</TableHead><TableHead className="text-right">খরচ / Expense</TableHead><TableHead>Customer Note</TableHead><TableHead>Status</TableHead>
            <TableHead>Pay status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-10 text-muted-foreground">{q ? "কোনো অর্ডার পাওয়া যায়নি।" : "No orders yet."}</TableCell></TableRow>}
            {filtered.map((r) => {
              const totalP = Number(r.total_price ?? 0);
              const paidP = Number(r.amount ?? 0);
              const dueP = Math.max(0, totalP - paidP);
              const expenseTotal = Number(r.govt_fee ?? 0) + Number(r.other_cost ?? 0) + Number(r.govt_charge_fee ?? 0);
              return (
              <TableRow key={r.id} id={`order-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.reference_id}</TableCell>
                <TableCell>
                  <div className="text-sm font-medium">{r.service_title}</div>
                  <div className="text-xs text-muted-foreground">{r.package_name}</div>
                </TableCell>
                <TableCell className="text-xs">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{r.customer_name ?? "—"}</span>
                    {b2bIds.has(r.user_id) && (
                      <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-[10px] px-1.5 py-0 h-4">B2B</Badge>
                    )}
                  </div>
                  <div className="text-muted-foreground">{r.customer_email}</div>
                  <div className="text-muted-foreground">{r.customer_phone}</div>
                </TableCell>
                <TableCell>
                  {Array.isArray(r.document_urls) && r.document_urls.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {r.document_urls.slice(0,3).map((u: string, i: number) => (
                        <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Doc {i+1}
                        </a>
                      ))}
                    </div>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-xs">
                  <div className="capitalize font-medium">{r.payment_method ?? "—"}</div>
                  {r.payment_transaction_id && (
                    <div className="text-muted-foreground"><span className="text-foreground/70">TrxID:</span> {r.payment_transaction_id}</div>
                  )}
                  {r.payment_sender_number && (
                    <div className="text-muted-foreground"><span className="text-foreground/70">Sender:</span> {r.payment_sender_number}</div>
                  )}
                  {r.payment_screenshot_url && <a href={r.payment_screenshot_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 mt-1"><ExternalLink className="h-3 w-3" /> Proof</a>}
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">
                  <div><span className="text-muted-foreground">Total:</span> <span className="font-semibold">৳{totalP.toLocaleString()}</span></div>
                  <div><span className="text-muted-foreground">Paid:</span> <span className="font-semibold text-emerald-600">৳{paidP.toLocaleString()}</span></div>
                  <div>
                    <span className="text-muted-foreground">Due:</span>{" "}
                    <span className={`font-bold ${dueP > 0 ? "text-red-600" : "text-emerald-600"}`}>৳{dueP.toLocaleString()}</span>
                  </div>
                  {Number(r.tax_amount ?? 0) > 0 && (
                    <Badge
                      variant="outline"
                      className={
                        ["verified", "paid", "completed"].includes(String(r.payment_status))
                          ? "mt-1 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-[10px]"
                          : "mt-1 bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30 text-[10px]"
                      }
                    >
                      {["verified", "paid", "completed"].includes(String(r.payment_status))
                        ? `✅ Tax Payment Received · ৳${Number(r.tax_amount).toLocaleString()}`
                        : `⏳ Tax Pending · ৳${Number(r.tax_amount).toLocaleString()}`}
                    </Badge>
                  )}
                  <PaymentUpdateBtn order={r} onUpdated={(patch) => setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, ...patch } : x))} />
                  <ExpenseUpdateBtn order={r} onUpdated={(patch) => setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, ...patch } : x))} />
                </TableCell>
                <TableCell className="text-right text-xs whitespace-nowrap">
                  <span className={`font-bold ${expenseTotal > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>৳{expenseTotal.toLocaleString()}</span>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {Number(r.govt_fee ?? 0) > 0 && <div>Govt: ৳{Number(r.govt_fee).toLocaleString()}</div>}
                    {Number(r.other_cost ?? 0) > 0 && <div>Other: ৳{Number(r.other_cost).toLocaleString()}</div>}
                    {Number(r.govt_charge_fee ?? 0) > 0 && <div>Charge: ৳{Number(r.govt_charge_fee).toLocaleString()}</div>}
                  </div>
                </TableCell>
                <TableCell className="text-xs max-w-[260px]">
                  {r.notes ? (
                    <div className="whitespace-pre-wrap break-words rounded-md bg-muted/50 p-2 text-foreground">{r.notes}</div>
                  ) : <span className="text-muted-foreground">—</span>}
                  <PortalCredentialsBlock order={r} />
                </TableCell>
                <TableCell>
                  <Select value={r.status} onValueChange={(v) => updateField(r.id, { status: v })}>
                    <SelectTrigger className="h-8 w-[130px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{ORDER_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Badge variant="outline" className={`${statusColor(r.status)} mt-1 text-xs`}>{r.status}</Badge>
                </TableCell>
                <TableCell>
                  <Select value={r.payment_status} onValueChange={(v) => updateField(r.id, { payment_status: v })}>
                    <SelectTrigger className="h-8 w-[170px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <CompactReceiptDialog order={r} compact />
                    <InvoiceBtn order={r} />
                    <InvoiceEditBtn order={r} onSaved={load} />
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Reset payment so customer can retry (clears amount + tx, sets status to pending)"
                      disabled={resettingOrderId === r.id}
                      onClick={async () => {
                        if (resettingOrderId) return;
                        if (!confirm("Reset this order's payment? This clears the transaction so the customer can re-submit payment.")) return;
                        setResettingOrderId(r.id);
                        const patch = {
                          payment_status: "pending_verification",
                          payment_transaction_id: null,
                          payment_sender_number: null,
                          amount: 0,
                        };
                        try {
                          await updateOrder({ data: { orderId: r.id, patch } });
                          setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, ...patch } : x));
                          toast.success("Payment reset — customer can now retry");
                        } catch (e: any) {
                          toast.error(e?.message ?? "Reset failed");
                        } finally {
                          setResettingOrderId(null);
                        }
                      }}
                    >
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                    </Button>
                    <BulkDownloadBtn order={r} />
                    <UploadInvoiceBtn order={r} onUploaded={(url) => setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, invoice_url: url, status: "completed" } : x))} />
                    <Button variant="ghost" size="icon" title="Archive (soft delete) — removes storage files, keeps revenue/history" onClick={() => archiveOrder(r)}>
                      <Archive className="h-4 w-4 text-amber-600" />
                    </Button>
                    <Button variant="ghost" size="icon" title="PERMANENTLY delete — purges files AND removes the row from revenue/analytics" onClick={() => hardDeleteOrder(r)}>
                      <Trash className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------- Bulk asset download (all docs for an order, dynamically named) ----------
function BulkDownloadBtn({ order }: { order: any }) {
  const [busy, setBusy] = useState(false);
  const count = Array.isArray(order.document_urls) ? order.document_urls.length : 0;
  if (count === 0) return null;
  return (
    <Button
      variant="ghost"
      size="icon"
      title={`Download all ${count} asset(s) as backup`}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const res = await downloadOrderAssets(order);
          toast.success(`Downloaded ${res.count} file(s) — ${res.archiveName}`);
        } catch (e: any) {
          toast.error(e?.message ?? "Download failed");
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageOpen className="h-4 w-4 text-primary" />}
    </Button>
  );
}

// ---------- Admin invoice upload button (file → invoices bucket → invoice_url) ----------
function UploadInvoiceBtn({ order, onUploaded }: { order: any; onUploaded: (url: string) => void }) {
  const [busy, setBusy] = useState(false);
  const uploadFn = useServerFn(adminUploadInvoice);
  const hasInvoice = !!order.invoice_url;
  return (
    <Button
      variant="ghost"
      size="icon"
      title={hasInvoice ? "Replace uploaded invoice file (visible to customer)" : "Upload invoice file (sends to customer dashboard)"}
      disabled={busy}
      onClick={() => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "application/pdf,image/*";
        input.onchange = async () => {
          const f = input.files?.[0];
          if (!f) return;
          if (f.size > 14 * 1024 * 1024) { toast.error("File too large (max 14MB)"); return; }
          setBusy(true);
          try {
            const buf = await f.arrayBuffer();
            let bin = "";
            const bytes = new Uint8Array(buf);
            const chunk = 0x8000;
            for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
            const base64 = btoa(bin);
            const res = await uploadFn({ data: { orderId: order.id, fileName: f.name, fileType: f.type || null, fileBase64: base64 } });
            toast.success("Invoice uploaded — customer can now download it");
            onUploaded(res.url);
          } catch (e: any) {
            toast.error(e?.message ?? "Upload failed");
          } finally {
            setBusy(false);
          }
        };
        input.click();
      }}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadFileIcon className={`h-4 w-4 ${hasInvoice ? "text-emerald-600" : "text-primary"}`} />}
    </Button>
  );
}

// ---------- Inline invoice download button ----------
function InvoiceBtn({ order }: { order: any }) {
  const { download, busyId } = useDownloadInvoice();
  const busy = busyId === order.id;
  return (
    <InvoiceDownloadControl
      busy={busy}
      label={null}
      compact
      onDownload={async (opts) => {
        try { await download(order, opts); } catch (e: any) { toast.error(e?.message || "Download failed"); }
      }}
    />
  );
}
function InvoiceEditBtn({ order, onSaved }: { order: any; onSaved?: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost" size="icon" title="Edit invoice for this order" onClick={() => setOpen(true)}>
        <Pencil className="h-4 w-4 text-primary" />
      </Button>
      {open && <OrderInvoiceEditor order={order} open={open} onOpenChange={setOpen} onSaved={onSaved} />}
    </>
  );
}

function PaymentUpdateBtn({ order, onUpdated }: { order: any; onUpdated: (patch: Record<string, any>) => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const totalP = Number(order.total_price ?? 0);
  const paidP = Number(order.amount ?? 0);
  const dueP = Math.max(0, totalP - paidP);
  const [totalVal, setTotalVal] = useState<string>(String(totalP));
  const [paidVal, setPaidVal] = useState<string>(String(paidP));
  const updateOrder = useServerFn(adminUpdateOrder);

  useEffect(() => {
    if (open) { setTotalVal(String(totalP)); setPaidVal(String(paidP)); }
  }, [open, totalP, paidP]);

  const newTotal = Number(totalVal || 0);
  const newPaid = Number(paidVal || 0);
  const newDue = Math.max(0, newTotal - newPaid);

  async function save() {
    if (!Number.isFinite(newTotal) || newTotal < 0) return toast.error("Invalid total");
    if (!Number.isFinite(newPaid) || newPaid < 0) return toast.error("Invalid paid amount");
    setSaving(true);
    try {
      const patch: Record<string, any> = { total_price: newTotal, amount: newPaid };
      if (newDue === 0 && newTotal > 0) patch.payment_status = "verified";
      await updateOrder({ data: { orderId: order.id, patch } });
      onUpdated(patch);
      toast.success("Payment updated");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Update failed");
    } finally { setSaving(false); }
  }

  async function clearDue() {
    setSaving(true);
    try {
      const patch: Record<string, any> = { amount: newTotal, payment_status: "verified" };
      await updateOrder({ data: { orderId: order.id, patch } });
      onUpdated(patch);
      toast.success("Due cleared");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally { setSaving(false); }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={`mt-1.5 h-7 text-[11px] ${dueP > 0 ? "border-red-300 text-red-700 hover:bg-red-50" : ""}`}
        onClick={() => setOpen(true)}
      >
        Update Payment
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update Payment · {order.reference_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Total Package Price (৳)</Label>
              <Input type="number" min="0" value={totalVal} onChange={(e) => setTotalVal(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Amount Paid (৳)</Label>
              <Input type="number" min="0" value={paidVal} onChange={(e) => setPaidVal(e.target.value)} className="mt-1" />
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 text-sm flex items-center justify-between">
              <span>Due Amount</span>
              <span className={`font-bold ${newDue > 0 ? "text-red-600" : "text-emerald-600"}`}>৳ {newDue.toLocaleString()}</span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={clearDue} disabled={saving || newTotal <= 0}>Mark Fully Paid</Button>
            <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------- Admin Expense (Govt Fee / Other Cost / Operational Notes) ----------
function ExpenseUpdateBtn({ order, onUpdated }: { order: any; onUpdated: (patch: Record<string, any>) => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [govtFee, setGovtFee] = useState<string>(String(order.govt_fee ?? 0));
  const [otherCost, setOtherCost] = useState<string>(String(order.other_cost ?? 0));
  const [opNotes, setOpNotes] = useState<string>(order.operational_notes ?? "");
  const updateOrder = useServerFn(adminUpdateOrder);

  useEffect(() => {
    if (open) {
      setGovtFee(String(order.govt_fee ?? 0));
      setOtherCost(String(order.other_cost ?? 0));
      setOpNotes(order.operational_notes ?? "");
    }
  }, [open, order]);

  async function save() {
    const gf = Number(govtFee || 0);
    const oc = Number(otherCost || 0);
    if (!Number.isFinite(gf) || gf < 0) return toast.error("Invalid Govt. Fee");
    if (!Number.isFinite(oc) || oc < 0) return toast.error("Invalid Other Cost");
    setSaving(true);
    try {
      const patch = { govt_fee: gf, other_cost: oc, operational_notes: opNotes || null };
      await updateOrder({ data: { orderId: order.id, patch } });
      onUpdated(patch);
      toast.success("Expense saved");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Update failed");
    } finally { setSaving(false); }
  }

  const hasExpense = Number(order.govt_fee ?? 0) > 0 || Number(order.other_cost ?? 0) > 0 || (order.operational_notes ?? "").trim();

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={`mt-1.5 h-7 text-[11px] ${hasExpense ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : ""}`}
        onClick={() => setOpen(true)}
        title="Admin expense for this order"
      >
        Expense
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Admin Expense · {order.reference_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Govt. Fee (৳)</Label>
              <Input type="number" min="0" value={govtFee} onChange={(e) => setGovtFee(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Other Cost (৳)</Label>
              <Input type="number" min="0" value={otherCost} onChange={(e) => setOtherCost(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Operational Notes</Label>
              <textarea
                value={opNotes}
                onChange={(e) => setOpNotes(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="Internal notes — not visible to customer"
              />
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 text-sm flex items-center justify-between">
              <span>Total Cost</span>
              <span className="font-bold">৳ {(Number(govtFee || 0) + Number(otherCost || 0)).toLocaleString()}</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}



function UsersPanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [b2bIds, setB2bIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const setTierFn = useServerFn(adminSetB2BTier);

  async function load() {
    const [{ data: profs }, { data: roles }, { data: staffRoles }] = await Promise.all([
      supabase.from("profiles").select("*").order("created_at", { ascending: false }),
      supabase.from("user_roles").select("user_id, role").eq("role", "b2b" as any),
      supabase.from("user_roles").select("user_id, role").in("role", ["admin", "moderator"] as any),
    ]);
    const staffIds = new Set((staffRoles ?? []).map((r: any) => r.user_id));
    setRows((profs ?? []).filter((p: any) => !staffIds.has(p.id)));
    setB2bIds(new Set((roles ?? []).map((r: any) => r.user_id)));
  }

  useEffect(() => { load(); }, []);

  async function toggleB2B(userId: string) {
    setBusy(userId);
    try {
      if (b2bIds.has(userId)) {
        const { error } = await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", "b2b" as any);
        if (error) throw error;
        toast.success("Set as General");
      } else {
        const { error } = await supabase.from("user_roles").insert({ user_id: userId, role: "b2b" as any });
        if (error) throw error;
        toast.success("Set as B2B");
      }
      await load();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to update");
    } finally {
      setBusy(null);
    }
  }

  async function toggleTier(userId: string, currentTier: string) {
    setBusy(userId);
    try {
      const next = currentTier === "vip" ? "standard" : "vip";
      await setTierFn({ data: { userId, tier: next } });
      toast.success(next === "vip" ? "Marked as B2B VIP" : "Marked as B2B Standard");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to update tier");
    } finally {
      setBusy(null);
    }
  }

  const [phoneQ, setPhoneQ] = useState("");
  const filtered = useMemo(() => {
    const q = phoneQ.trim().toLowerCase();
    if (!q) return rows;
    const digits = q.replace(/\D/g, "");
    return rows.filter((p) => {
      const name = (p.full_name ?? "").toString().toLowerCase();
      const email = (p.email ?? "").toString().toLowerCase();
      const phone = (p.phone ?? "").toString().replace(/\D/g, "");
      if (name.includes(q) || email.includes(q)) return true;
      if (digits && phone.includes(digits)) return true;
      return false;
    });
  }, [rows, phoneQ]);

  const blockUserFn = useServerFn(adminSetUserBlock);
  async function toggleBlock(p: any) {
    const willBlock = !p.is_blocked;
    if (willBlock) {
      const reason = window.prompt(`Block ${p.full_name || p.email || "this user"}?\nOptional reason (shown to user):`, "");
      if (reason === null) return; // cancelled
      setBusy(p.id);
      try {
        await blockUserFn({ data: { userId: p.id, blocked: true, reason: reason || null } });
        toast.success("User blocked");
        await load();
      } catch (e: any) { toast.error(e?.message ?? "Failed"); }
      finally { setBusy(null); }
    } else {
      if (!window.confirm("Unblock this user?")) return;
      setBusy(p.id);
      try {
        await blockUserFn({ data: { userId: p.id, blocked: false } });
        toast.success("User unblocked");
        await load();
      } catch (e: any) { toast.error(e?.message ?? "Failed"); }
      finally { setBusy(null); }
    }
  }

  function exportUsersCsv() {
    const escape = (v: any) => {
      const s = (v ?? "").toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const header = ["Name", "Email", "Phone", "Joined"];
    const lines = [header.join(",")];
    for (const p of filtered) {
      const joined = p.created_at ? new Date(p.created_at).toLocaleDateString() : "";
      lines.push([escape(p.full_name ?? ""), escape(p.email ?? ""), escape(p.phone ?? ""), escape(joined)].join(","));
    }
    const csv = "\ufeff" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `users-${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} users`);
  }

  // Block-manager dialog state (separate from main user search)
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockQ, setBlockQ] = useState("");
  const blockedList = useMemo(() => rows.filter((p) => p.is_blocked), [rows]);
  const blockSearchList = useMemo(() => {
    const q = blockQ.trim().toLowerCase();
    if (!q) return [] as typeof rows;
    const digits = q.replace(/\D/g, "");
    return rows.filter((p) => {
      const name = (p.full_name ?? "").toString().toLowerCase();
      const email = (p.email ?? "").toString().toLowerCase();
      const phone = (p.phone ?? "").toString().replace(/\D/g, "");
      if (name.includes(q) || email.includes(q)) return true;
      if (digits && phone.includes(digits)) return true;
      return false;
    }).slice(0, 25);
  }, [rows, blockQ]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={phoneQ}
            onChange={(e) => setPhoneQ(e.target.value)}
            placeholder="Search by name, email or phone…"
            className="pl-9"
          />
        </div>
        {phoneQ && <Button size="sm" variant="ghost" onClick={() => setPhoneQ("")}>Clear</Button>}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setBlockOpen(true)}
          title="Block / Unblock users"
          className="text-red-600 hover:text-red-700 hover:bg-red-500/10 border-red-500/30"
        >
          <Ban className="h-4 w-4 mr-1.5" /> Block
          {blockedList.length > 0 && (
            <Badge variant="destructive" className="ml-1.5 h-5 text-[10px] px-1.5">{blockedList.length}</Badge>
          )}
        </Button>
        <Button size="sm" variant="outline" onClick={exportUsersCsv} disabled={filtered.length === 0} title="Download as Excel/CSV">
          <Download className="h-4 w-4 mr-1.5" /> Download
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} / {rows.length}</span>
      </div>

      <div className="glass-strong rounded-2xl overflow-hidden no-hover-lift">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Joined</TableHead><TableHead>Type</TableHead><TableHead>B2B Tier</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">{phoneQ ? "No users match this phone number." : "No users yet."}</TableCell></TableRow>}
            {filtered.map((p) => {
              const isB2B = b2bIds.has(p.id);
              const tier = (p.b2b_tier === "vip" ? "vip" : "standard") as "vip" | "standard";
              return (
                <TableRow key={p.id} className={p.is_blocked ? "bg-red-500/5" : ""}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{p.full_name ?? "—"}</span>
                      {p.is_blocked && <Badge variant="destructive" className="text-[10px] h-5">Blocked</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>{p.email ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{p.phone ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {isB2B
                      ? <Badge className="bg-gradient-to-br from-amber-400 to-orange-600 text-white border-0">B2B</Badge>
                      : <Badge variant="secondary">General</Badge>}
                  </TableCell>
                  <TableCell>
                    {isB2B ? (
                      <div className="flex items-center gap-2">
                        {tier === "vip"
                          ? <Badge className="bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white border-0">VIP</Badge>
                          : <Badge variant="outline">Standard</Badge>}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy === p.id}
                          onClick={() => toggleTier(p.id, tier)}
                          className="h-7 text-[11px]"
                        >
                          {busy === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : (tier === "vip" ? "Make Standard" : "Make VIP")}
                        </Button>
                      </div>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 justify-end">
                      <Button
                        size="sm"
                        variant={isB2B ? "outline" : "default"}
                        disabled={busy === p.id}
                        onClick={() => toggleB2B(p.id)}
                        className={!isB2B ? "gradient-primary text-primary-foreground shadow-glow hover:opacity-90" : ""}
                      >
                        {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isB2B ? "Make General" : "Make B2B")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-600" />
              User Block Manager
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                autoFocus
                value={blockQ}
                onChange={(e) => setBlockQ(e.target.value)}
                placeholder="Search user by name, email or phone to block/unblock…"
                className="pl-9"
              />
            </div>

            {blockQ.trim() && (
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-muted-foreground px-1">Search results</div>
                <div className="max-h-64 overflow-y-auto rounded-xl border divide-y">
                  {blockSearchList.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">No users found.</div>
                  ) : blockSearchList.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3 p-2.5">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-2">
                          {p.full_name ?? "—"}
                          {p.is_blocked && <Badge variant="destructive" className="text-[10px] h-4">Blocked</Badge>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {p.email ?? "—"}{p.phone ? ` · ${p.phone}` : ""}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={p.is_blocked ? "outline" : "destructive"}
                        disabled={busy === p.id}
                        onClick={() => toggleBlock(p)}
                        className="shrink-0"
                      >
                        {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (p.is_blocked ? <><ShieldCheck className="h-3.5 w-3.5 mr-1" />Unblock</> : <><Ban className="h-3.5 w-3.5 mr-1" />Block</>)}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground px-1">
                Blocked users ({blockedList.length})
              </div>
              <div className="max-h-64 overflow-y-auto rounded-xl border divide-y">
                {blockedList.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">No blocked users.</div>
                ) : blockedList.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 p-2.5 bg-red-500/5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.full_name ?? "—"}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {p.email ?? "—"}{p.phone ? ` · ${p.phone}` : ""}
                      </div>
                      {p.blocked_reason && (
                        <div className="text-[11px] text-red-600 truncate mt-0.5">Reason: {p.blocked_reason}</div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === p.id}
                      onClick={() => toggleBlock(p)}
                      className="shrink-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                    >
                      {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ShieldCheck className="h-3.5 w-3.5 mr-1" />Unblock</>}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------- B2B Pricing ----------
function B2BPricingPanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    const [pkgRes, visaRes] = await Promise.all([
      supabase.from("packages").select("id, name, slug, service_code, price, active").order("name", { ascending: true }),
      supabase.from("visa_tax_packages").select("id, name, price, active").order("name", { ascending: true }),
    ]);
    if (pkgRes.error) toast.error(pkgRes.error.message);
    if (visaRes.error) toast.error(visaRes.error.message);
    const { data: b2bRows, error: b2bError } = await supabase
      .from("package_b2b_prices")
      .select("package_table, package_id, price")
      .in("package_table", ["packages", "visa_tax_packages"]);
    if (b2bError) toast.error(b2bError.message);
    const b2bByKey = new Map((b2bRows ?? []).map((r: any) => [`${r.package_table}:${r.package_id}`, r.price]));
    const merged = [
      ...((pkgRes.data ?? []).map((r: any) => ({ ...r, b2b_price: b2bByKey.get(`packages:${r.id}`) ?? null, _source: "packages" }))),
      ...((visaRes.data ?? []).map((r: any) => ({
        id: r.id,
        name: r.name,
        slug: null,
        service_code: "visa-tax",
        price: r.price,
        b2b_price: b2bByKey.get(`visa_tax_packages:${r.id}`) ?? null,
        active: r.active,
        _source: "visa_tax_packages",
      }))),
    ].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    setRows(merged);
    setDrafts(Object.fromEntries(merged.map((r: any) => [r.id, r.b2b_price ?? ""])));
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save(id: string) {
    setSavingId(id);
    try {
      const row = rows.find((r) => r.id === id);
      const table = row?._source === "visa_tax_packages" ? "visa_tax_packages" : "packages";
      const value = (drafts[id] ?? "").trim();
      if (value !== "" && Number.isNaN(Number(value))) {
        throw new Error("B2B price must be a number");
      }
      const { error } = value === ""
        ? await supabase.from("package_b2b_prices").delete().eq("package_table", table).eq("package_id", id)
        : await supabase.from("package_b2b_prices").upsert({ package_table: table, package_id: id, price: value }, { onConflict: "package_table,package_id" });
      if (error) throw error;
      toast.success("B2B price saved");
      await load();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save");
    } finally {
      setSavingId(null);
    }
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => (r.name ?? "").toLowerCase().includes(needle) || (r.slug ?? "").toLowerCase().includes(needle) || (r.service_code ?? "").toLowerCase().includes(needle));
  }, [rows, q]);

  return (
    <div className="rounded-3xl p-6 md:p-8 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-rose-500/15 border-2 border-amber-500/40 shadow-[0_20px_60px_-15px_rgba(245,158,11,0.35)] relative overflow-hidden">
      <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-orange-500/15 blur-3xl pointer-events-none" />
      <div className="relative flex items-center gap-4 mb-6">
        <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-600 text-white font-display font-black text-3xl flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(245,158,11,0.6)] ring-4 ring-amber-300/40">B</div>
        <div className="flex-1">
          <h2 className="font-display font-black text-2xl bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600 bg-clip-text text-transparent">B2B Pricing</h2>
          <p className="text-sm text-muted-foreground">All packages (A → Z). Edit B2B-only prices. Leave empty to hide B2B pricing for that package.</p>
        </div>
      </div>
      <div className="relative mb-4">
        <Input placeholder="Search package name, slug, service code…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md border-amber-500/30 focus-visible:ring-amber-500/50 bg-background/70" />
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
      ) : (
        <div className="relative glass-strong rounded-2xl overflow-hidden border border-amber-500/20">
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-rose-500/10 hover:bg-amber-500/15 border-amber-500/30">
                <TableHead className="text-amber-700 dark:text-amber-300 font-bold uppercase tracking-wider text-xs">Package</TableHead>
                <TableHead className="text-amber-700 dark:text-amber-300 font-bold uppercase tracking-wider text-xs">Service Code</TableHead>
                <TableHead className="text-amber-700 dark:text-amber-300 font-bold uppercase tracking-wider text-xs">General Price</TableHead>
                <TableHead className="text-amber-700 dark:text-amber-300 font-bold uppercase tracking-wider text-xs">B2B Price</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No packages found.</TableCell></TableRow>}
              {filtered.map((r) => {
                const hasB2B = r.b2b_price != null && r.b2b_price !== "";
                return (
                <TableRow key={r.id} className="hover:bg-amber-500/5 transition-colors">
                  <TableCell>
                    <div className="font-semibold flex items-center gap-2">
                      {r.name}
                      {r._source === "visa_tax_packages" && (
                        <Badge className="bg-sky-500/15 text-sky-600 border border-sky-500/30 text-[10px]">Visa Tax</Badge>
                      )}
                      {hasB2B && (
                        <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 text-[10px]">B2B set</Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{r.slug ?? r._source}{!r.active && " · inactive"}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-500/10 text-slate-700 dark:text-slate-300 border border-slate-500/20 font-mono">
                      {r.service_code ?? "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">৳ {r.price}</span>
                  </TableCell>
                  <TableCell>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-600 dark:text-amber-400 font-bold pointer-events-none">৳</span>
                      <Input
                        value={drafts[r.id] ?? ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                        placeholder="e.g. 1200"
                        className="max-w-[180px] pl-7 border-amber-500/40 focus-visible:ring-amber-500 bg-amber-500/5 font-mono font-semibold text-amber-700 dark:text-amber-300"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      disabled={savingId === r.id || (drafts[r.id] ?? "") === (r.b2b_price ?? "")}
                      onClick={() => save(r.id)}
                      className="bg-gradient-to-br from-amber-400 via-orange-500 to-rose-600 text-white hover:opacity-90 hover:shadow-[0_8px_20px_-5px_rgba(245,158,11,0.6)] transition-all"
                    >
                      {savingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                    </Button>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
      )}
      <div className="mt-8">
        <B2BUsersList />
      </div>
    </div>
  );
}

// ---------- B2B Users List (inside B2B tab) ----------
function B2BUsersList() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data: roles } = await supabase
      .from("user_roles")
      .select("user_id")
      .eq("role", "b2b" as any);
    const ids = (roles ?? []).map((r: any) => r.user_id);
    if (ids.length === 0) { setUsers([]); setLoading(false); return; }
    const { data: profs } = await supabase
      .from("profiles")
      .select("*")
      .in("id", ids)
      .order("created_at", { ascending: false });
    setUsers(profs ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function action(userId: string, mode: "general" | "suspend") {
    if (mode === "suspend" && !confirm("Suspend this B2B user? They will lose B2B pricing access.")) return;
    setBusy(userId);
    try {
      const { error } = await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", "b2b" as any);
      if (error) throw error;
      toast.success(mode === "suspend" ? "B2B access suspended" : "Converted back to General");
      await load();
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="glass-strong rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center gap-2">
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-600 text-white font-display font-black">B</span>
        <h3 className="font-display font-bold text-lg">B2B Users ({users.length})</h3>
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No B2B users yet. Promote users from the Users tab.</TableCell></TableRow>}
            {users.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.full_name ?? "—"}</TableCell>
                <TableCell>{p.email ?? "—"}</TableCell>
                <TableCell className="font-mono text-xs">{p.phone ?? "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === p.id}
                      onClick={() => action(p.id, "general")}
                    >
                      {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Convert to General"}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy === p.id}
                      onClick={() => action(p.id, "suspend")}
                    >
                      Suspend
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ---------- Home Packages wrapper with Sync from Packages ----------
function HomePackagesWrapper({ config }: { config: CrudTable }) {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="space-y-4">
      <SyncFromPackagesPanel onSynced={() => setRefreshKey((k) => k + 1)} />
      <CrudPanel key={refreshKey} config={config} />
    </div>
  );
}

function SyncFromPackagesPanel({ onSynced }: { onSynced: () => void }) {
  const [pkgs, setPkgs] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("packages")
        .select("id, name, name_bn, description, description_bn, price, delivery_days, delivery_days_bn, features, features_bn, popular, display_order, active")
        .order("name", { ascending: true });
      if (error) toast.error(error.message);
      else setPkgs((data ?? []) as any[]);
      setLoading(false);
    })();
  }, []);

  async function syncSelected() {
    if (!selectedId) {
      toast.error("একটি প্যাকেজ নির্বাচন করুন");
      return;
    }
    const pkg = pkgs.find((p) => p.id === selectedId);
    if (!pkg) return;
    setSyncing(true);
    try {
      const priceStr = String(pkg.price ?? "").trim();
      const isNumeric = /^\d[\d,.\s]*$/.test(priceStr);
      const formattedPrice = isNumeric ? `৳ ${priceStr}` : priceStr || "Quote";
      const priceType = isNumeric ? "amount" : "quote";

      const payload: any = {
        name: pkg.name,
        name_bn: pkg.name_bn ?? null,
        tagline: pkg.description ?? null,
        tagline_bn: pkg.description_bn ?? null,
        price: formattedPrice,
        price_type: priceType,
        delivery: pkg.delivery_days ?? null,
        delivery_bn: pkg.delivery_days_bn ?? null,
        features: Array.isArray(pkg.features) ? pkg.features : [],
        features_bn: Array.isArray(pkg.features_bn) ? pkg.features_bn : [],
        popular: !!pkg.popular,
        display_order: pkg.display_order ?? 0,
        active: true,
      };

      // Upsert by name to avoid duplicates
      const { data: existing } = await (supabase.from as any)("home_packages")
        .select("id").eq("name", pkg.name).maybeSingle();

      if (existing?.id) {
        const { error } = await (supabase.from as any)("home_packages")
          .update(payload).eq("id", existing.id);
        if (error) throw error;
        toast.success(`"${pkg.name}" আপডেট হয়েছে হোম প্যাকেজে`);
      } else {
        const { error } = await (supabase.from as any)("home_packages").insert(payload);
        if (error) throw error;
        toast.success(`"${pkg.name}" হোম প্যাকেজে যোগ হয়েছে`);
      }
      setSelectedId("");
      onSynced();
    } catch (e: any) {
      toast.error(e?.message ?? "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="glass rounded-2xl p-5 border border-primary/20">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="h-5 w-5 text-primary" />
        <h3 className="text-base font-semibold">অটো সিঙ্ক — All Packages থেকে হোম প্যাকেজে যোগ করুন</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        নিচের তালিকা থেকে একটি প্যাকেজ নির্বাচন করুন এবং "Sync to Home" চাপুন। প্যাকেজটি স্বয়ংক্রিয়ভাবে হোম পেইজের প্যাকেজ সেকশনে কপি হয়ে যাবে (একই নাম থাকলে আপডেট হবে)।
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={selectedId} onValueChange={setSelectedId} disabled={loading || syncing}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={loading ? "লোড হচ্ছে…" : "একটি প্যাকেজ নির্বাচন করুন"} />
          </SelectTrigger>
          <SelectContent>
            {pkgs.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}{p.price ? ` — ${p.price}` : ""}{p.active ? "" : " (inactive)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={syncSelected} disabled={!selectedId || syncing}>
          {syncing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Syncing…</> : <><Plus className="h-4 w-4 mr-2" /> Sync to Home</>}
        </Button>
      </div>
    </div>
  );
}

// ---------- Generic CRUD ----------

function CrudPanel({ config }: { config: CrudTable }) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  async function load() {
    setLoading(true);
    let q = supabase.from(config.table as any).select("*");
    if (config.orderBy) q = q.order(config.orderBy.col, { ascending: config.orderBy.ascending });
    else q = q.order("created_at", { ascending: false });
    const { data, error } = await q;
    if (error) toast.error(error.message); else setRows((data ?? []) as any[]);
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [config.table]);

  function openNew() { setEditing({ ...(config.defaults ?? {}) }); setOpen(true); }
  function openEdit(r: any) { setEditing({ ...r }); setOpen(true); }
  async function del(id: string) {
    if (!confirm("Delete this row?")) return;
    const { error } = await supabase.from(config.table as any).delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    setRows((rs) => rs.filter((r) => r.id !== id));
  }

  async function toggleField(id: string, key: string, value: boolean) {
    const prev = rows;
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
    const { error } = await supabase.from(config.table as any).update({ [key]: value }).eq("id", id);
    if (error) {
      setRows(prev);
      toast.error(error.message);
    } else {
      toast.success(value ? "Activated" : "Deactivated");
    }
  }

  async function save() {
    if (!editing) return;
    // Validate JSON fields
    const payload: any = {};
    for (const f of config.fields) {
      let v = editing[f.key];
      if (f.type === "number" && v !== "" && v != null) v = Number(v);
      if (f.type === "boolean" || f.type === "switch") v = !!v;
      if (f.type === "datetime" && typeof v === "string" && v) {
        const d = new Date(v);
        if (!isNaN(d.getTime())) v = d.toISOString();
      }
      if (f.type === "percent") {
        const m = String(v ?? "").match(/(\d+(\.\d+)?)/);
        v = m ? `${m[1]}%` : null;
      }
      if (f.type === "json" && typeof v === "string") {
        try { v = v.trim() ? JSON.parse(v) : []; } catch { return toast.error(`Invalid JSON in ${f.label}`); }
      }
      if (f.required && (v == null || v === "")) return toast.error(`${f.label} is required`);
      // Convert empty optional strings to null so timestamp/date/numeric columns don't fail Postgres parsing
      if (typeof v === "string" && v === "" && !f.required) v = null;
      if (v !== undefined) payload[f.key] = v;
    }
    // Coerce jsonb-array columns: TagsField emits a comma-separated string,
    // but some columns (e.g. knowledge_hub.seo_keywords) are jsonb arrays.
    if (config.table === "knowledge_hub" && "seo_keywords" in payload) {
      const k = payload.seo_keywords;
      if (typeof k === "string") {
        payload.seo_keywords = k.split(",").map((s: string) => s.trim()).filter(Boolean);
      } else if (k == null) {
        payload.seo_keywords = [];
      }
    }
    if (editing.id) {
      const { error } = await supabase.from(config.table as any).update(payload).eq("id", editing.id);
      if (error) return toast.error(error.message);
      toast.success("Saved");
    } else {
      const { error } = await supabase.from(config.table as any).insert(payload);
      if (error) return toast.error(error.message);
      toast.success("Created");
    }
    setOpen(false); setEditing(null); load();
    // Real-time refresh of public site (meta tags / JSON-LD) and Live Ranking Dashboard
    queryClient.invalidateQueries({ queryKey: [config.table] });
    if (config.table === "seo_settings") {
      queryClient.invalidateQueries({ queryKey: ["seo_settings"] });
      queryClient.invalidateQueries({ queryKey: ["seo_settings_keywords_live"] });
    }
    if (config.table === "site_settings") {
      queryClient.invalidateQueries({ queryKey: ["site_settings"] });
    }
  }

  // Singleton mode: skip table, edit the single row directly
  useEffect(() => {
    if (config.singleton && rows.length > 0 && !open && !editing) {
      // no-op; show inline edit
    }
  }, [rows, config.singleton, open, editing]);

  if (config.singleton) {
    const row = rows[0];
    return (
      <div className="glass-strong rounded-2xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display font-bold">{config.label}</h3>
          {row ? <Button onClick={() => openEdit(row)} variant="outline"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
               : <Button onClick={openNew} className="gradient-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Create</Button>}
        </div>
        {row && (
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {config.fields.map((f) => (
              <div key={f.key} className="rounded-lg bg-background/60 border p-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</div>
                <div className="mt-1 break-words">{formatVal(row[f.key])}</div>
              </div>
            ))}
          </div>
        )}
        <EditDialog open={open} setOpen={setOpen} editing={editing} setEditing={setEditing} fields={config.fields} onSave={save} label={config.label} existingRows={rows} />
      </div>
    );
  }

  return (
    <div className="glass-strong rounded-2xl overflow-hidden no-hover-lift">
      <div className="flex justify-between items-center p-4">
        <h3 className="font-display font-bold">{config.label} ({rows.length})</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}</Button>
          <Button size="sm" onClick={openNew} className="gradient-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> New</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            {config.listCols.map((c) => <TableHead key={c}>{c}</TableHead>)}
            <TableHead className="w-[100px]"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={config.listCols.length + 1} className="text-center py-10 text-muted-foreground">No rows.</TableCell></TableRow>}
            {rows.map((r) => {
              const isActive = (r as any).active !== false && (r as any).published !== false;
              return (
              <TableRow key={r.id} className={isActive ? "" : "opacity-60 bg-muted/30"}>
                {config.listCols.map((c) => (
                  <TableCell key={c} className="max-w-[280px] truncate">
                    {(c === "active" || c === "published") && typeof r[c] === "boolean" ? (
                      <div className="flex items-center gap-2">
                        <Switch checked={!!r[c]} onCheckedChange={(v) => toggleField(r.id, c, v)} />
                        <Badge variant={r[c] ? "default" : "secondary"} className={r[c] ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30" : ""}>
                          {r[c] ? (c === "published" ? "Published" : "Active") : (c === "published" ? "Draft" : "Inactive")}
                        </Badge>
                      </div>
                    ) : formatVal(r[c])}
                  </TableCell>
                ))}
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </div>
      <EditDialog open={open} setOpen={setOpen} editing={editing} setEditing={setEditing} fields={config.fields} onSave={save} label={config.label} existingRows={rows} />
    </div>
  );
}

function formatVal(v: any): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "✓" : "✗";
  if (Array.isArray(v) || typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/**
 * IME-safe field: keeps local state during composition (Bangla/Avro/Ridmik)
 * so parent re-renders don't drop characters. Syncs up on every change once
 * composition is committed.
 */
function TextField({
  id, value, onCommit, multiline, type, className, rows,
}: {
  id: string;
  value: string;
  onCommit: (v: string) => void;
  multiline?: boolean;
  type?: "text" | "number";
  className?: string;
  rows?: number;
}) {
  const [local, setLocal] = useState<string>(value ?? "");
  const composingRef = React.useRef(false);

  useEffect(() => {
    if (!composingRef.current) setLocal(value ?? "");
  }, [value]);

  const commonProps = {
    id,
    value: local,
    lang: "bn",
    onCompositionStart: () => { composingRef.current = true; },
    onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      composingRef.current = false;
      const v = (e.target as HTMLInputElement | HTMLTextAreaElement).value;
      setLocal(v);
      onCommit(v);
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setLocal(e.target.value);
      if (!composingRef.current) onCommit(e.target.value);
    },
    onBlur: () => { if (local !== value) onCommit(local); },
    className,
  };

  if (multiline) {
    return <Textarea {...(commonProps as any)} rows={rows ?? 3} />;
  }
  return <Input {...(commonProps as any)} type={type === "number" ? "number" : "text"} />;
}

// SEO lint rules for live validation in admin forms.
// - Title-like fields recommended 30–60 chars (hard cap warning at 70).
// - Description/excerpt-like fields recommended 120–160 chars (hard cap at 200).
// - Duplicate detection across existing rows for title/slug/seo_title fields.
const SEO_LIMITS: Record<string, { min: number; max: number; hard: number; kind: "title" | "desc" }> = {
  title: { min: 30, max: 60, hard: 70, kind: "title" },
  title_bn: { min: 20, max: 60, hard: 70, kind: "title" },
  seo_title: { min: 30, max: 60, hard: 70, kind: "title" },
  seo_title_bn: { min: 20, max: 60, hard: 70, kind: "title" },
  site_title: { min: 30, max: 60, hard: 70, kind: "title" },
  excerpt: { min: 50, max: 160, hard: 200, kind: "desc" },
  excerpt_bn: { min: 50, max: 160, hard: 200, kind: "desc" },
  seo_description: { min: 120, max: 160, hard: 200, kind: "desc" },
  seo_description_bn: { min: 120, max: 160, hard: 200, kind: "desc" },
  site_description: { min: 120, max: 160, hard: 200, kind: "desc" },
};
const DUP_FIELDS = new Set(["title", "title_bn", "slug", "seo_title", "seo_title_bn"]);

function lintSeoField(key: string, value: any, editing: any, existingRows: any[] = []) {
  const v = typeof value === "string" ? value : "";
  const len = v.trim().length;
  const limit = SEO_LIMITS[key];
  const issues: { level: "error" | "warn"; msg: string }[] = [];
  if (limit && len > 0) {
    if (len > limit.hard) {
      issues.push({ level: "error", msg: `Too long: ${len} chars (max ${limit.max}).` });
    } else if (len > limit.max) {
      issues.push({ level: "error", msg: `Over recommended ${limit.max} chars (now ${len}).` });
    } else if (len < limit.min) {
      issues.push({ level: "warn", msg: `Short: ${len}/${limit.min}–${limit.max} recommended.` });
    } else {
      issues.push({ level: "warn", msg: `✓ ${len} chars (good).` });
    }
  }
  if (DUP_FIELDS.has(key) && v.trim()) {
    const norm = v.trim().toLowerCase();
    const dup = existingRows.find((r: any) => r?.id !== editing?.id && typeof r?.[key] === "string" && r[key].trim().toLowerCase() === norm);
    if (dup) issues.push({ level: "error", msg: `Duplicate of an existing entry — must be unique.` });
  }
  // Cross-field duplicate inside the same record (e.g. seo_title === title)
  if (key === "seo_title" && v.trim() && editing?.title && v.trim().toLowerCase() === String(editing.title).trim().toLowerCase()) {
    issues.push({ level: "warn", msg: `SEO title same as Title — consider a unique variation.` });
  }
  if (key === "seo_description" && v.trim() && editing?.excerpt && v.trim().toLowerCase() === String(editing.excerpt).trim().toLowerCase()) {
    issues.push({ level: "warn", msg: `SEO description same as Excerpt — consider rewriting for search.` });
  }
  return issues;
}

function SeoLintBadge({ issues }: { issues: { level: "error" | "warn"; msg: string }[] }) {
  if (!issues.length) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {issues.map((i, idx) => (
        <p key={idx} className={`text-xs ${i.level === "error" ? "text-red-600 dark:text-red-400 font-medium" : "text-muted-foreground"}`}>
          {i.msg}
        </p>
      ))}
    </div>
  );
}

function EditDialog({ open, setOpen, editing, setEditing, fields, onSave, label, existingRows = [] }: any) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const isPopup = label === "Popup Offers";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing?.id ? `Edit ${label}` : `New ${label}`}</DialogTitle></DialogHeader>
        {editing && (
          <div className="space-y-3">
            {fields.map((f: FieldDef) => {
              const issues = (f.type === "text" || f.type === "textarea") ? lintSeoField(f.key, editing[f.key], editing, existingRows) : [];
              const hasError = issues.some((i) => i.level === "error");
              const errorBorder = hasError ? "border-red-500 ring-1 ring-red-500/40 focus-visible:ring-red-500/60" : "";
              return (
              <div key={f.key}>
                <Label htmlFor={f.key} className={hasError ? "text-red-600 dark:text-red-400" : ""}>{f.label}{f.required && " *"}</Label>
                {f.type === "textarea" ? (
                  <TextField
                    id={f.key}
                    multiline
                    value={editing[f.key] ?? ""}
                    onCommit={(v) => setEditing((prev: any) => ({ ...prev, [f.key]: v }))}
                    className={`mt-1 ${errorBorder}`}
                  />
                ) : f.type === "boolean" ? (
                  <div className="mt-1">
                    <input type="checkbox" id={f.key} checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} className="h-4 w-4" />
                  </div>
                ) : f.type === "switch" ? (
                  <div className="mt-2 flex items-center gap-3 rounded-lg border bg-muted/40 px-4 py-3">
                    <Switch id={f.key} checked={!!editing[f.key]} onCheckedChange={(c) => setEditing({ ...editing, [f.key]: c })} />
                    <span className={`text-sm font-semibold ${editing[f.key] ? "text-emerald-600" : "text-muted-foreground"}`}>
                      {editing[f.key] ? "ON — Campaign is LIVE on homepage" : "OFF — Campaign is HIDDEN sitewide"}
                    </span>
                  </div>
                ) : f.type === "datetime" ? (
                  <input
                    id={f.key}
                    type="datetime-local"
                    step={1}
                    value={(() => {
                      const v = editing[f.key];
                      if (!v) return "";
                      const d = new Date(v);
                      if (isNaN(d.getTime())) return typeof v === "string" ? v.slice(0, 16) : "";
                      const pad = (n: number) => String(n).padStart(2, "0");
                      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                    })()}
                    onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                    className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                ) : f.type === "percent" ? (
                  <div className="mt-1 relative">
                    <input
                      id={f.key}
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={(() => { const m = String(editing[f.key] ?? "").match(/(\d+(\.\d+)?)/); return m ? m[1] : ""; })()}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm font-semibold"
                      placeholder="e.g. 10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">%</span>
                  </div>
                ) : f.type === "json" ? (
                  <TextField
                    id={f.key}
                    multiline
                    rows={5}
                    value={typeof editing[f.key] === "string" ? editing[f.key] : JSON.stringify(editing[f.key] ?? [], null, 2)}
                    onCommit={(v) => setEditing((prev: any) => ({ ...prev, [f.key]: v }))}
                    className="mt-1 font-mono text-xs"
                  />
                ) : f.type === "image" ? (
                  <ImageUploadField value={editing[f.key] ?? ""} onChange={(url) => setEditing({ ...editing, [f.key]: url })} />
                ) : f.type === "crop_image" ? (
                  <CropImageUploadField value={editing[f.key] ?? ""} onChange={(url) => setEditing({ ...editing, [f.key]: url })} />
                ) : f.type === "tags" ? (
                  <TagsField
                    value={editing[f.key] ?? ""}
                    onChange={(v) => setEditing((prev: any) => ({ ...prev, [f.key]: v }))}
                  />
                ) : f.type === "seo_multi" ? (
                  <SeoMultiSelectField
                    value={editing[f.key]}
                    onChange={(arr) => setEditing((prev: any) => ({ ...prev, [f.key]: arr }))}
                  />
                ) : f.type === "select" ? (
                  <select
                    id={f.key}
                    value={editing[f.key] ?? ""}
                    onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                    className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">— select —</option>
                    {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <TextField
                    id={f.key}
                    type={f.type === "number" ? "number" : "text"}
                    value={editing[f.key] ?? ""}
                    onCommit={(v) => setEditing((prev: any) => ({ ...prev, [f.key]: v }))}
                    className={`mt-1 ${errorBorder}`}
                  />
                )}
                <SeoLintBadge issues={issues} />
              </div>
            );})}
          </div>
        )}
        <DialogFooter>
          {isPopup && (
            <Button variant="outline" onClick={() => setPreviewOpen(true)}>Preview</Button>
          )}
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={onSave} className="gradient-primary text-primary-foreground">Save</Button>
        </DialogFooter>
        {isPopup && previewOpen && editing && (
          <PopupPreview data={editing} onClose={() => setPreviewOpen(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PopupPreview({ data, onClose }: { data: any; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-card shadow-glow border border-border/60 overflow-hidden"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-background transition-colors shadow-soft"
        >
          ×
        </button>
        {data.image_url && (
          <img src={data.image_url} alt={data.title} className="w-full h-48 object-cover" />
        )}
        <div className="p-6">
          <h3 className="font-display font-bold text-2xl gradient-text">{data.title || "Untitled"}</h3>
          {data.message && (
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{data.message}</p>
          )}
          {data.cta_text && data.cta_link && (
            <a
              href={data.cta_link}
              target="_blank" rel="noopener noreferrer"
              className="mt-5 inline-flex items-center justify-center w-full h-11 rounded-xl gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-95 transition-opacity"
            >
              {data.cta_text}
            </a>
          )}
        </div>
        <div className="px-6 py-3 border-t bg-muted/30 text-xs text-muted-foreground text-center">
          Preview — this is how the popup will appear on the site
        </div>
      </div>
    </div>
  );
}

function TagsField({ value, onChange }: { value: string | string[] | null | undefined; onChange: (v: string) => void }) {
  const valStr = Array.isArray(value)
    ? value.join(", ")
    : (typeof value === "string" ? value : "");
  const tags = valStr
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const [input, setInput] = useState("");
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const commit = (next: string[]) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of next) {
      const k = t.toLowerCase();
      if (!seen.has(k) && t) {
        seen.add(k);
        out.push(t);
      }
    }
    onChange(out.join(", "));
  };

  const addFromInput = () => {
    const parts = input.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    commit([...tags, ...parts]);
    setInput("");
  };

  const removeAt = (i: number) => {
    const next = tags.slice();
    next.splice(i, 1);
    commit(next);
  };

  const startEdit = (i: number) => {
    setEditingIdx(i);
    setEditingValue(tags[i]);
  };

  const saveEdit = () => {
    if (editingIdx === null) return;
    const v = editingValue.trim();
    const next = tags.slice();
    if (!v) {
      next.splice(editingIdx, 1);
    } else {
      next[editingIdx] = v;
    }
    commit(next);
    setEditingIdx(null);
    setEditingValue("");
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setEditingValue("");
  };

  return (
    <div className="mt-1 space-y-2">
      <div className="flex flex-wrap gap-2 rounded-md border border-input bg-background p-2 min-h-[44px]">
        {tags.length === 0 && (
          <span className="text-xs text-muted-foreground self-center px-1">No keywords yet — add one below.</span>
        )}
        {tags.map((t, i) =>
          editingIdx === i ? (
            <span
              key={`edit-${i}`}
              className="inline-flex items-center gap-1 rounded-full bg-background border border-emerald-400 px-1.5 py-0.5"
            >
              <Input
                autoFocus
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveEdit();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    cancelEdit();
                  }
                }}
                onBlur={saveEdit}
                className="h-6 w-32 text-xs px-2"
              />
            </span>
          ) : (
            <span
              key={`${t}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-400/30 px-2.5 py-1 text-xs font-medium"
            >
              <button
                type="button"
                onClick={() => startEdit(i)}
                className="hover:underline focus:outline-none"
                title="Click to edit"
              >
                {t}
              </button>
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="rounded-full hover:bg-emerald-200/70 dark:hover:bg-emerald-400/20 p-0.5"
                aria-label={`Remove ${t}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          )
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addFromInput();
            } else if (e.key === "Backspace" && input === "" && tags.length > 0) {
              removeAt(tags.length - 1);
            }
          }}
          placeholder="Type a keyword and press Enter or +"
        />
        <Button type="button" onClick={addFromInput} className="gradient-primary text-primary-foreground">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Click any keyword to edit it. Tags save into the site's meta keywords automatically.
      </p>
    </div>
  );
}

function SeoMultiSelectField({ value, onChange }: { value: any; onChange: (arr: string[]) => void }) {
  const [available, setAvailable] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState("");

  // Normalize current value -> string[]
  const selected: string[] = Array.isArray(value)
    ? value.map(String)
    : typeof value === "string"
      ? (() => { try { const j = JSON.parse(value); return Array.isArray(j) ? j.map(String) : value.split(",").map((s: string) => s.trim()).filter(Boolean); } catch { return value.split(",").map((s) => s.trim()).filter(Boolean); } })()
      : [];

  useEffect(() => {
    supabase.from("seo_settings").select("keywords").order("created_at", { ascending: false }).limit(1).maybeSingle()
      .then(({ data }) => {
        const raw = (data as any)?.keywords ?? "";
        const list = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
        // dedupe (case-insensitive) but keep original casing
        const seen = new Set<string>();
        const dedup: string[] = [];
        for (const k of list) {
          const lk = k.toLowerCase();
          if (!seen.has(lk)) { seen.add(lk); dedup.push(k); }
        }
        setAvailable(dedup);
      });
  }, []);

  const toggle = (tag: string) => {
    const lk = tag.toLowerCase();
    if (selected.some((s) => s.toLowerCase() === lk)) {
      onChange(selected.filter((s) => s.toLowerCase() !== lk));
    } else {
      onChange([...selected, tag]);
    }
  };

  const addCustom = () => {
    const parts = customInput.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const merged = [...selected];
    for (const p of parts) {
      if (!merged.some((s) => s.toLowerCase() === p.toLowerCase())) merged.push(p);
    }
    onChange(merged);
    setCustomInput("");
  };

  return (
    <div className="mt-1 space-y-3">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 rounded-md border border-input bg-background p-2">
          {selected.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary border border-primary/30 px-2.5 py-0.5 text-xs font-semibold">
              {t}
              <button type="button" onClick={() => toggle(t)} aria-label={`Remove ${t}`} className="hover:bg-primary/20 rounded-full p-0.5">
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {available.length === 0 ? (
        <p className="text-xs text-muted-foreground">No SEO keywords set yet — add some in the SEO settings, or type custom tags below.</p>
      ) : (
        <div>
          <p className="text-[11px] text-muted-foreground mb-1.5">Click to toggle keywords from SEO settings:</p>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto rounded-md border border-dashed border-input p-2">
            {available.map((t) => {
              const isOn = selected.some((s) => s.toLowerCase() === t.toLowerCase());
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggle(t)}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium border transition-colors ${
                    isOn ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-input hover:bg-muted"
                  }`}
                >
                  {isOn && <CheckIcon />}{t}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addCustom(); } }}
          placeholder="Add a custom tag (Enter or comma)"
        />
        <Button type="button" onClick={addCustom} className="gradient-primary text-primary-foreground">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function CheckIcon() {
  return <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4L8.5 12l6.8-6.7a1 1 0 011.4 0z" clipRule="evenodd"/></svg>;
}

function ImageUploadField({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const [uploading, setUploading] = useState(false);


  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a PNG or JPG image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `popups/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("public-assets").upload(path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("public-assets").getPublicUrl(path);
      onChange(data.publicUrl);
      toast.success("Image uploaded");
    } catch (e: any) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-1 space-y-2">
      {value && (
        <div className="relative inline-block">
          <img src={value} alt="" className="h-24 w-auto rounded-lg border object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center shadow"
            aria-label="Remove"
          >
            ×
          </button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = "";
          }}
          disabled={uploading}
          className="flex-1"
        />
        {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
      </div>
      <Input
        type="text"
        placeholder="Or paste image URL"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs"
      />
    </div>
  );
}

function CropImageUploadField({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [aspect, setAspect] = useState<number>(1);
  const [areaPixels, setAreaPixels] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [bgTransparent, setBgTransparent] = useState(true);

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) { toast.error("PNG/JPG/WebP image please"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setSrc(reader.result as string); setZoom(1); setCrop({ x: 0, y: 0 }); };
    reader.readAsDataURL(file);
  };

  const getCroppedBlob = (): Promise<Blob> => new Promise((resolve, reject) => {
    if (!src || !areaPixels) return reject(new Error("No crop"));
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 512;
      canvas.width = size;
      canvas.height = Math.round(size / aspect);
      const ctx = canvas.getContext("2d")!;
      if (!bgTransparent) { ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height); }
      ctx.drawImage(
        img,
        areaPixels.x, areaPixels.y, areaPixels.width, areaPixels.height,
        0, 0, canvas.width, canvas.height
      );
      canvas.toBlob((b) => b ? resolve(b) : reject(new Error("blob failed")), "image/png", 0.95);
    };
    img.onerror = () => reject(new Error("image load failed"));
    img.src = src;
  });

  const saveCropped = async () => {
    setUploading(true);
    try {
      const blob = await getCroppedBlob();
      const path = `payment-logos/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.png`;
      const { error } = await supabase.storage.from("public-assets").upload(path, blob, { contentType: "image/png", upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from("public-assets").getPublicUrl(path);
      onChange(data.publicUrl);
      setSrc(null);
      toast.success("লোগো সংরক্ষিত হয়েছে");
    } catch (e: any) {
      toast.error(e.message || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="mt-1 space-y-3">
      {value && !src && (
        <div className="flex items-center gap-3">
          <div className="h-20 w-20 rounded-lg border bg-[conic-gradient(at_50%_50%,#f3f4f6_0_25%,#e5e7eb_0_50%,#f3f4f6_0_75%,#e5e7eb_0)] bg-[length:12px_12px] flex items-center justify-center overflow-hidden">
            <img src={value} alt="" className="max-h-full max-w-full object-contain" />
          </div>
          <button type="button" onClick={() => onChange("")} className="text-xs text-destructive underline">Remove</button>
        </div>
      )}

      {!src && (
        <Input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }}
        />
      )}

      {src && (
        <div className="space-y-3 rounded-xl border bg-muted/30 p-3">
          <div className="relative w-full h-72 rounded-lg overflow-hidden bg-neutral-900">
            <CropperLazy
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_a: any, px: any) => setAreaPixels(px)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div>
              <label className="font-semibold">Zoom (ছোট/বড়)</label>
              <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full" />
            </div>
            <div>
              <label className="font-semibold">Shape</label>
              <select value={aspect} onChange={(e) => setAspect(Number(e.target.value))} className="mt-1 w-full h-9 rounded-md border bg-background px-2">
                <option value={1}>Square 1:1</option>
                <option value={4 / 3}>4:3</option>
                <option value={16 / 9}>16:9</option>
                <option value={3 / 4}>3:4</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={bgTransparent} onChange={(e) => setBgTransparent(e.target.checked)} />
                Transparent background
              </label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={saveCropped} disabled={uploading || !areaPixels}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              সংরক্ষণ করুন
            </Button>
            <Button type="button" variant="outline" onClick={() => setSrc(null)}>বাতিল</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const CropperLazy = (props: any) => {
  const [Comp, setComp] = useState<any>(null);
  useEffect(() => { import("react-easy-crop").then((m) => setComp(() => m.default)); }, []);
  if (!Comp) return <div className="absolute inset-0 flex items-center justify-center text-white/60 text-xs">Loading…</div>;
  return <Comp {...props} />;
};

// ---------- Analytics ----------
function AnalyticsPanel() {
  const [stats, setStats] = useState<any>(null);
  useEffect(() => {
    (async () => {
      const [orders, users, services, packagesC, posts, popups] = await Promise.all([
        supabase.from("orders").select("status,payment_status,amount,created_at"),
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("services").select("id", { count: "exact", head: true }),
        supabase.from("packages").select("id", { count: "exact", head: true }),
        supabase.from("blog_posts").select("id", { count: "exact", head: true }),
        supabase.from("popup_offers").select("id", { count: "exact", head: true }),
      ]);
      const rows = orders.data ?? [];
      const since = Date.now() - 7 * 24 * 3600 * 1000;
      setStats({
        users: users.count ?? 0,
        services: services.count ?? 0,
        packages: packagesC.count ?? 0,
        posts: posts.count ?? 0,
        popups: popups.count ?? 0,
        orders: rows.length,
        revenue: rows.filter((r: any) => r.payment_status === "verified").reduce((s: number, r: any) => s + Number(r.amount ?? 0), 0),
        pending: rows.filter((r: any) => r.status === "pending").length,
        processing: rows.filter((r: any) => r.status === "processing").length,
        completed: rows.filter((r: any) => r.status === "completed").length,
        last7: rows.filter((r: any) => new Date(r.created_at).getTime() > since).length,
      });
    })();
  }, []);
  if (!stats) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const cards = [
    { label: "Revenue (verified)", value: `৳ ${stats.revenue.toLocaleString()}`, icon: CreditCard, accent: true },
    { label: "Total orders", value: stats.orders, icon: ShoppingCart },
    { label: "Last 7 days", value: stats.last7, icon: BarChart3 },
    { label: "Users", value: stats.users, icon: Users },
    { label: "Pending", value: stats.pending, icon: Loader2 },
    { label: "Processing", value: stats.processing, icon: Truck },
    { label: "Completed", value: stats.completed, icon: ShieldCheck },
    { label: "Services", value: stats.services, icon: FileText },
    { label: "Packages", value: stats.packages, icon: CreditCard },
    { label: "Blog posts", value: stats.posts, icon: BookOpen },
    { label: "Popup offers", value: stats.popups, icon: Megaphone },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.label} className={`glass rounded-2xl p-5 shadow-card ${c.accent ? "ring-1 ring-primary/30" : ""}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</p>
              <p className="font-display text-2xl font-bold mt-1">{c.value}</p>
            </div>
            <div className="h-10 w-10 rounded-xl gradient-primary text-primary-foreground flex items-center justify-center shadow-glow">
              <c.icon className="h-5 w-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Delivery ----------
const DELIVERY_STATUSES = ["not_started", "in_progress", "delivered", "on_hold"] as const;
function DeliveryPanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, { url: string; files: File[]; busy: boolean }>>({});
  const deliverOrder = useServerFn(adminDeliverOrder);
  const updateOrderFn = useServerFn(adminUpdateOrder);
  async function load() {
    setLoading(true);
    const { data, error } = await supabase.from("orders")
      .select("id,reference_id,customer_name,customer_phone,service_title,package_name,status,delivery_status,delivery_notes,delivery_url,delivered_at")
      .in("status", ["processing", "ready", "completed"])
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message); else setRows(data ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);
  async function update(id: string, patch: any) {
    if (patch.delivery_status === "delivered" && !patch.delivered_at) patch.delivered_at = new Date().toISOString();
    try {
      await updateOrderFn({ data: { orderId: id, patch } });
      setRows((rs) => rs.map((r) => r.id === id ? { ...r, ...patch } : r));
      return true;
    } catch (e: any) { toast.error(e?.message ?? "Update failed"); return false; }
  }
  function waLink(phone?: string | null) {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, "");
    return digits ? `https://wa.me/${digits}` : null;
  }
  async function fileToBase64(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as any);
    }
    return btoa(binary);
  }
  async function completeDelivery(r: any) {
    const draft = drafts[r.id] ?? { url: r.delivery_url ?? "", files: [] as File[], busy: false };
    const url = draft.url.trim();
    if (!url && draft.files.length === 0) return toast.error("Choose one or more files or paste a delivery URL");
    setDrafts((d) => ({ ...d, [r.id]: { ...draft, busy: true } }));
    try {
      const payloadFiles: Array<{ name: string; type: string; base64: string }> = [];
      for (const f of draft.files) {
        if (f.size > 25 * 1024 * 1024) throw new Error(`File too large (max 25MB): ${f.name}`);
        payloadFiles.push({
          name: f.name,
          type: f.type || "application/octet-stream",
          base64: await fileToBase64(f),
        });
      }
      const res = await deliverOrder({ data: { orderId: r.id, url: url || null, files: payloadFiles } });
      setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, delivery_url: res.url, delivery_status: "delivered", status: "completed", delivered_at: new Date().toISOString() } : x));
      toast.success(`Delivery completed (${payloadFiles.length} file${payloadFiles.length === 1 ? "" : "s"})`);
      setDrafts((d) => ({ ...d, [r.id]: { url: "", files: [], busy: false } }));
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
      setDrafts((d) => ({ ...d, [r.id]: { ...(d[r.id] ?? draft), busy: false } }));
    }
  }
  return (
    <div className="glass-strong rounded-2xl overflow-hidden no-hover-lift">
      <div className="flex justify-between items-center p-4">
        <h3 className="font-display font-bold">Delivery queue ({rows.length})</h3>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}</Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Ref</TableHead><TableHead>Customer</TableHead><TableHead>Service</TableHead>
            <TableHead>Delivery status</TableHead><TableHead className="min-w-[320px]">Action</TableHead><TableHead>Notes</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Nothing to deliver.</TableCell></TableRow>}
            {rows.map((r) => {
              const wa = waLink(r.customer_phone);
              const draft = drafts[r.id] ?? { url: r.delivery_url ?? "", files: [] as File[], busy: false };
              return (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.reference_id}</TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{r.customer_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{r.customer_phone ?? "no phone"}</div>
                    {wa ? (
                      <a href={wa} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 px-2 py-1 rounded-md bg-green-600 text-white text-xs font-semibold hover:bg-green-700">
                        WhatsApp
                      </a>
                    ) : (
                      <span className="inline-block mt-1 text-[10px] text-muted-foreground">No WhatsApp</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{r.service_title}<div className="text-xs text-muted-foreground">{r.package_name}</div></TableCell>
                  <TableCell>
                    <Select value={r.delivery_status ?? "not_started"} onValueChange={(v) => update(r.id, { delivery_status: v })}>
                      <SelectTrigger className="h-8 w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{DELIVERY_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                    {r.delivered_at && <div className="text-[10px] text-muted-foreground mt-1">{new Date(r.delivered_at).toLocaleDateString()}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1.5">
                      <Input value={draft.url} placeholder="Paste delivery URL (optional)"
                        className="h-8 w-[300px]"
                        onChange={(e) => { const v = e.target.value; setDrafts((d) => ({ ...d, [r.id]: { ...(d[r.id] ?? { url: "", files: [], busy: false }), url: v } })); }} />
                      <Input type="file" multiple className="h-8 w-[300px] text-xs"
                        onChange={(e) => { const fs = Array.from(e.target.files ?? []); setDrafts((d) => ({ ...d, [r.id]: { ...(d[r.id] ?? { url: "", files: [], busy: false }), files: fs } })); }} />
                      {draft.files.length > 0 && (
                        <div className="text-[10px] text-muted-foreground w-[300px] truncate">
                          {draft.files.length} file{draft.files.length === 1 ? "" : "s"} selected: {draft.files.map((f) => f.name).join(", ")}
                        </div>
                      )}

                      {r.delivery_status === "delivered" ? (
                        <Button size="sm" className="h-8 w-[300px] bg-gray-300 text-gray-800 hover:bg-gray-300 cursor-default" disabled>
                          ✓ Delivered
                        </Button>
                      ) : (
                        <Button size="sm" className="h-8 w-[300px] gradient-primary text-primary-foreground"
                          disabled={draft.busy} onClick={() => completeDelivery(r)}>
                          {draft.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Complete Delivery"}
                        </Button>
                      )}
                      {r.delivery_url && (
                        <div className="flex flex-col gap-0.5 w-[300px]">
                          {r.delivery_url.split("\n").filter(Boolean).map((u: string, i: number) => (
                            <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline truncate">
                              File {i + 1}: {u}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-start gap-1">
                      <Input defaultValue={r.delivery_notes ?? ""} placeholder="Notes" className="h-8 w-[200px]"
                        onBlur={(e) => e.target.value !== (r.delivery_notes ?? "") && update(r.id, { delivery_notes: e.target.value || null })} />
                      <InvoiceBtn order={r} />
                      <InvoiceEditBtn order={r} />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------- Backup ----------
function BackupPanel() {
  const [busy, setBusy] = useState(false);
  const exportTables = [
    "hero_section","services","packages","faq","testimonials","contact_info","payment_settings",
    "blog_posts","knowledge_hub","seo_settings","site_settings","whatsapp_numbers","popup_offers","orders","profiles",
  ];
  async function exportAll() {
    setBusy(true);
    try {
      const dump: Record<string, any> = { exported_at: new Date().toISOString() };
      for (const t of exportTables) {
        const { data, error } = await supabase.from(t as any).select("*");
        if (error) throw error;
        dump[t] = data ?? [];
      }
      const blob = new Blob([JSON.stringify(dump, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `backup-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e: any) {
      toast.error(e.message ?? "Backup failed");
    } finally { setBusy(false); }
  }
  return (
    <div className="space-y-4">
      <div className="glass-strong rounded-2xl p-6">
        <h3 className="font-display font-bold flex items-center gap-2"><Database className="h-5 w-5 text-primary" /> Backup & maintenance</h3>
        <p className="text-sm text-muted-foreground mt-1">Download a JSON snapshot of every site table. Store it safely — restore by re-importing rows via the admin panel.</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl bg-background/60 border p-4">
            <p className="font-semibold text-sm">Full export</p>
            <p className="text-xs text-muted-foreground mt-1">Includes content, orders, users & settings.</p>
            <Button onClick={exportAll} disabled={busy} className="mt-3 w-full gradient-primary text-primary-foreground">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> Download backup</>}
            </Button>
          </div>
          <div className="rounded-xl bg-background/60 border p-4">
            <p className="font-semibold text-sm">Tables included</p>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{exportTables.join(", ")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Profit & Expense Tracker (বিজনেস প্রফিট ও এক্সপেন্স ট্র্যাকার) ----------
type ProfitRange = "30d" | "month" | "year" | "all";
const PROFIT_RANGES: { value: ProfitRange; label: string }[] = [
  { value: "30d", label: "Last 30 Days" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "all", label: "All Time" },
];

function ProfitPanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState<ProfitRange>("30d");

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("orders")
      .select("id,reference_id,service_title,package_name,amount,total_price,tax_amount,govt_charge_fee,govt_fee,other_cost,operational_notes,payment_status,created_at")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    else setRows(data ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const filteredRows = useMemo(() => {
    if (range === "all") return rows;
    const now = new Date();
    let cutoff: Date;
    if (range === "30d") {
      cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 30);
    } else if (range === "month") {
      cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      cutoff = new Date(now.getFullYear(), 0, 1);
    }
    return rows.filter((r) => r.created_at && new Date(r.created_at) >= cutoff);
  }, [rows, range]);

  const verified = filteredRows.filter((r) => r.payment_status === "verified");
  const totalRevenue = verified.reduce((s, r) => s + Number(r.amount ?? 0), 0);
  const govtExpenses = filteredRows.reduce((s, r) => s + Number(r.govt_fee ?? 0), 0);
  const opCosts = filteredRows.reduce((s, r) => s + Number(r.other_cost ?? 0), 0);
  const netProfit = totalRevenue - (govtExpenses + opCosts);

  const cards = [
    { label: "Total Revenue", value: totalRevenue, color: "from-emerald-500 to-emerald-700", icon: "💰" },
    { label: "Govt Expenses", value: govtExpenses, color: "from-amber-500 to-orange-600", icon: "🏛️" },
    { label: "Operational Costs", value: opCosts, color: "from-rose-500 to-red-600", icon: "⚙️" },
    { label: "Net Profit", value: netProfit, color: netProfit >= 0 ? "from-sky-500 to-blue-700" : "from-red-500 to-red-700", icon: "📈" },
  ];

  const rangeLabel = PROFIT_RANGES.find((r) => r.value === range)?.label ?? "";

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold">বিজনেস প্রফিট ও এক্সপেন্স ট্র্যাকার</h2>
          <p className="text-sm text-muted-foreground mt-1">Real-time revenue, government expenses, operational costs, and net profit. Filter by date range below.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">Range:</span>
        {PROFIT_RANGES.map((r) => (
          <Button
            key={r.value}
            size="sm"
            variant={range === r.value ? "default" : "outline"}
            onClick={() => setRange(r.value)}
            className="h-8"
          >
            {r.label}
          </Button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground">Showing <b className="text-foreground">{filteredRows.length}</b> orders</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-2xl p-5 bg-gradient-to-br ${c.color} text-white shadow-card`}>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider opacity-90 font-semibold">{c.label}</p>
              <span className="text-2xl">{c.icon}</span>
            </div>
            <p className="font-display text-3xl font-bold mt-3">৳ {Math.round(c.value).toLocaleString()}</p>
            <p className="text-[10px] opacity-80 mt-1">{rangeLabel}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border bg-muted/30 p-4 text-xs text-muted-foreground">
        <p><b className="text-foreground">Formula:</b> Net Profit = Total Revenue − (Govt Expenses + Operational Costs)</p>
        <p className="mt-1">Total Revenue counts only orders with payment status <b>verified</b>. Govt Expenses and Operational Costs come from the admin <b>Expense</b> dialog on each order.</p>
      </div>

      <div className="glass-strong rounded-2xl overflow-hidden no-hover-lift">
        <div className="p-4 border-b">
          <h3 className="font-display font-bold">Per-order breakdown — {rangeLabel} ({filteredRows.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ref</TableHead>
                <TableHead>Service</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Govt Fee</TableHead>
                <TableHead className="text-right">Other Cost</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No orders in this range.</TableCell></TableRow>}
              {filteredRows.map((r) => {
                const rev = r.payment_status === "verified" ? Number(r.amount ?? 0) : 0;
                const gf = Number(r.govt_fee ?? 0);
                const oc = Number(r.other_cost ?? 0);
                const profit = rev - (gf + oc);
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.reference_id}</TableCell>
                    <TableCell className="text-xs">{r.service_title}<div className="text-muted-foreground">{r.package_name}</div></TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600">৳{rev.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-amber-600">৳{gf.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-rose-600">৳{oc.toLocaleString()}</TableCell>
                    <TableCell className={`text-right font-bold ${profit >= 0 ? "text-sky-600" : "text-red-600"}`}>৳{profit.toLocaleString()}</TableCell>
                    <TableCell className="text-xs max-w-[260px]">
                      {r.operational_notes ? <div className="whitespace-pre-wrap break-words text-muted-foreground">{r.operational_notes}</div> : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

// ---------- Revenue Report ----------

type RevenueCategory = "Income Tax" | "VAT" | "Company Services" | "Other";
const REVENUE_CATEGORIES: RevenueCategory[] = ["Income Tax", "VAT", "Company Services", "Other"];

function categorizeOrder(slug?: string | null, title?: string | null): RevenueCategory {
  const s = `${slug ?? ""} ${title ?? ""}`.toLowerCase();
  if (/\bvat\b|\bbin\b|ভ্যাট|ভ্যট/.test(s)) return "VAT";
  if (/income[\s-]?tax|tax[\s-]?return|tax[\s-]?consult|tax[\s-]?calc|\btin\b|e[\s-]?tin|আয়কর|ট্যাক্স|কর/.test(s)) return "Income Tax";
  if (/company|trade[\s-]?license|\berc\b|\birc\b|registration|business/.test(s)) return "Company Services";
  return "Other";
}

function RevenuePanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState<string>(String(new Date().getFullYear()));
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("orders")
        .select("id,reference_id,customer_name,customer_email,customer_phone,service_slug,service_title,package_name,amount,payment_status,status,created_at")
        .order("created_at", { ascending: false });
      if (error) toast.error(error.message);
      setRows(data ?? []);
      setLoading(false);
    })();
  }, []);

  const years = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => set.add(String(new Date(r.created_at).getFullYear())));
    const arr = Array.from(set).sort((a, b) => Number(b) - Number(a));
    if (arr.length === 0) arr.push(String(new Date().getFullYear()));
    return arr;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (year !== "all" && String(new Date(r.created_at).getFullYear()) !== year) return false;
      if (!q) return true;
      return [r.customer_name, r.customer_email, r.customer_phone, r.reference_id, r.service_title]
        .some((v) => (v ?? "").toString().toLowerCase().includes(q));
    });
  }, [rows, year, search]);

  const verified = useMemo(() => filtered.filter((r) => r.payment_status === "verified"), [filtered]);

  const byCategory = useMemo(() => {
    const acc: Record<RevenueCategory, { revenue: number; orders: number }> = {
      "Income Tax": { revenue: 0, orders: 0 },
      "VAT": { revenue: 0, orders: 0 },
      "Company Services": { revenue: 0, orders: 0 },
      "Other": { revenue: 0, orders: 0 },
    };
    verified.forEach((r) => {
      const cat = categorizeOrder(r.service_slug, r.service_title);
      acc[cat].revenue += Number(r.amount ?? 0);
      acc[cat].orders += 1;
    });
    return acc;
  }, [verified]);

  const totalRevenue = useMemo(
    () => Object.values(byCategory).reduce((s, c) => s + c.revenue, 0),
    [byCategory],
  );
  const totalOrders = useMemo(
    () => Object.values(byCategory).reduce((s, c) => s + c.orders, 0),
    [byCategory],
  );

  const monthly = useMemo(() => {
    const arr = Array.from({ length: 12 }, (_, i) => ({ month: i, revenue: 0, orders: 0 }));
    verified.forEach((r) => {
      const m = new Date(r.created_at).getMonth();
      arr[m].revenue += Number(r.amount ?? 0);
      arr[m].orders += 1;
    });
    return arr;
  }, [verified]);

  function exportCsv() {
    const header = ["Reference", "Date", "Customer", "Phone", "Email", "Category", "Service", "Package", "Amount (BDT)", "Payment", "Status"];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      const cat = categorizeOrder(r.service_slug, r.service_title);
      const cells = [
        r.reference_id,
        new Date(r.created_at).toISOString().slice(0, 10),
        r.customer_name,
        r.customer_phone,
        r.customer_email,
        cat,
        r.service_title,
        r.package_name,
        r.amount,
        r.payment_status,
        r.status,
      ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`);
      lines.push(cells.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const maxMonth = Math.max(1, ...monthly.map((m) => m.revenue));

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="glass rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[160px]">
          <Label className="text-xs">Year</Label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All years</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Search</Label>
          <Input className="mt-1" placeholder="Customer, phone, reference…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button variant="outline" onClick={exportCsv} className="gap-1.5">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="glass rounded-2xl p-5 shadow-card ring-1 ring-primary/30">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Revenue</p>
          <p className="font-display text-2xl font-bold mt-1">৳ {totalRevenue.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">{totalOrders} verified orders</p>
        </div>
        {REVENUE_CATEGORIES.map((cat) => (
          <div key={cat} className="glass rounded-2xl p-5 shadow-card">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{cat}</p>
            <p className="font-display text-xl font-bold mt-1">৳ {byCategory[cat].revenue.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">{byCategory[cat].orders} orders</p>
          </div>
        ))}
      </div>

      {/* Monthly chart */}
      <div className="glass rounded-2xl p-5">
        <p className="font-semibold text-sm mb-3">Monthly revenue ({year})</p>
        <div className="grid grid-cols-12 gap-2 h-40 items-end">
          {monthly.map((m) => (
            <div key={m.month} className="flex flex-col items-center gap-1">
              <div
                className="w-full rounded-t gradient-primary"
                style={{ height: `${(m.revenue / maxMonth) * 100}%`, minHeight: m.revenue > 0 ? 4 : 0 }}
                title={`৳ ${m.revenue.toLocaleString()}`}
              />
              <span className="text-[10px] text-muted-foreground">{["J","F","M","A","M","J","J","A","S","O","N","D"][m.month]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Orders table */}
      <div className="glass rounded-2xl p-2 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Service</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Payment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No orders match this filter.</TableCell></TableRow>
            )}
            {filtered.map((r) => {
              const cat = categorizeOrder(r.service_slug, r.service_title);
              return (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="font-mono text-xs">{r.reference_id}</TableCell>
                  <TableCell>
                    <div className="font-medium">{r.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{r.customer_phone}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{cat}</Badge></TableCell>
                  <TableCell className="text-xs">{r.service_title}<div className="text-muted-foreground">{r.package_name}</div></TableCell>
                  <TableCell className="text-right font-semibold">৳ {Number(r.amount ?? 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant={r.payment_status === "verified" ? "default" : "outline"}>{r.payment_status}</Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
